/* eslint-disable @typescript-eslint/no-empty-function */


import React, { useCallback, useMemo, useState, useContext, useEffect } from 'react'
import { Link as RouterLink, useLocation, useNavigate, useSearchParams } from 'react-router-dom'

import {
  CircleDashed,
  CircleSlash,
  CircleCheck,
  ArrowLeftRight,
  HelpCircle,
  ShieldAlert,
  // §21 — the custody pair takes MAP-PIN glyphs, not package ones. A package glyph
  // already carries the tag-replacement SHIPMENT meaning elsewhere in this app
  // (CertificatePage's "Mark as received" and its held-replacement banner), and
  // these two actions are about a PLACE: dispatch to a location, then confirm it
  // got there. Pin-plus / pin-check keep the pair reading as one flow, and must
  // stay identical to CertificatePage's copies of the same two menu items.
  MapPinPlusIcon,
  MapPinCheckIcon,
} from 'lucide-react'

import { CERTIFICATE_STATUSES, CERT_TRANSFER_STATUSES } from 'constants/statuses'
import { DevToolsContext } from 'contexts'

// StatusChip-style filter options (leading icon + status color) so the certificates
// Status filter matches the scans-tab status selectors (parity across data tables).
const STATUS_ICON = { size: 14, strokeWidth: 2 } as const
const CERT_STATUS_FILTER_ICONS: Record<string, React.ReactElement> = {
  created: <CircleDashed {...STATUS_ICON} />,
  inactive: <CircleSlash {...STATUS_ICON} />,
  active: <CircleCheck {...STATUS_ICON} />,
  // Keys track CertificateStatus — see the note in constants/statuses.ts.
  //
  // ⚠️ ALL FOUR in-flight transfer stages take the SAME glyph, deliberately, and it is
  // `ArrowLeftRight` — the app's transfer glyph everywhere else: every Transfer action
  // (the row kebabs here, the bulk bar, the toolbar button, CertificatePage, PeoplePage,
  // OwnerPage), all twelve transfer rows in the certificate Activity timeline, and — the
  // one that settles it — the Scans tab's own "In Transfer" STATUS chip. So this is not
  // "action glyph vs status glyph"; the product already has a transfer status glyph and
  // this map was the only place disagreeing with it.
  //
  // What was here: `ArrowUpRight` on the two `transfer_*` keys, with a comment reading
  // "both transfer stages point outward". Up-and-right is this app's NAVIGATE glyph
  // (§21) — "View live", external links, reference links — so one glyph carried two
  // meanings, and a filter chip labelled "Transfer" wore the icon that elsewhere means
  // "this opens somewhere else". `ArrowLeftRight` says what a transfer is: a change of
  // hands between two parties, which `ArrowRight` ("next") does not.
  //
  // Uniformity across the four is load-bearing, not tidiness. `CERT_STATUS_FILTER_OPTIONS`
  // below keeps ONE option per LABEL and all four of these label "Transfer", so exactly one
  // survives — whichever `CERTIFICATE_STATUSES` happens to declare first, in another file.
  // `waiting_accept`'s `Clock` and `cancel_request`'s `CircleX` were therefore already
  // dead, and which of the four rendered was a function of key order in a file nobody
  // editing this one would think to check. With all four identical the chip is the same
  // glyph regardless of that order.
  transfer_initiated: <ArrowLeftRight {...STATUS_ICON} />,
  transfer_waiting_confirmation: <ArrowLeftRight {...STATUS_ICON} />,
  waiting_accept: <ArrowLeftRight {...STATUS_ICON} />,
  cancel_request: <ArrowLeftRight {...STATUS_ICON} />,
  lost: <HelpCircle {...STATUS_ICON} />,
  stolen: <ShieldAlert {...STATUS_ICON} />,
}
// One option per LABEL, not per status. The three in-flight transfer stages all
// read "Transfer" (see constants/statuses.ts), so deriving 1:1 would offer three
// identical rows each matching a third of the transfers. Keeping the first
// occurrence gives one "Transfer" row; `expandStatusFilter` below turns it back
// into all three values when the filter is applied.
const CERT_STATUS_FILTER_OPTIONS = Object.entries(CERTIFICATE_STATUSES)
  .map(([value, cfg]) => ({
    value,
    label: (cfg as any).label,
    color: (cfg as any).color,
    // The authored fill/edge, not just the hue — so a status chip in this filter is the same
    // colour as the same status in the table's Status column (which renders all three).
    backgroundColor: (cfg as any).backgroundColor,
    borderColor: (cfg as any).borderColor,
    icon: CERT_STATUS_FILTER_ICONS[value],
  }))
  .filter((opt, i, all) => all.findIndex(o => o.label === opt.label) === i)

/** Turn the collapsed "Transfer" option back into every status it stands for. */
const expandStatusFilter = (selected: string[]): string[] =>
  Array.from(
    new Set(
      selected.flatMap(v =>
        (CERT_TRANSFER_STATUSES as readonly string[]).includes(v)
          ? [...CERT_TRANSFER_STATUSES]
          : [v],
      ),
    ),
  )

import { useDispatch } from 'store/hooks'
import { startScanSession } from 'store/scan-session/actions'
import {
  IValidationScanSessionMessageCallbackParams,
  IValidationStartScanSessionCallbackParams,
} from 'store/scan-session/types'
import { getErrorMessage, isCertificateInTransferState, getTransferDisabledMessage, isBrandHeldCertificate, canSplitCertificateGroup, TRANSFER_PERMISSION_TOOLTIP, CUSTODY_RECEIVE_PERMISSION_TOOLTIP, isTransferAvailable, isActivateAvailable, isCancelTransferAvailable, isReplaceTagAvailable, isDeleteAvailable } from 'utils'
import { datetime } from 'utils/date'
import { createApiCall } from 'services'
import { useQueries } from '@tanstack/react-query'

import type { CertificateAdmin, CertificateStatus } from 'types'
import type { CertificateGroupData } from 'hooks/api'
import { useOpenCustodyHops, useCustodyMutations } from 'hooks/api'

import { DEFAULT_COMPANY_ID } from 'config'
import { useSnackbar } from 'contexts'

import { useCertificates, useProductsLastImport, useCanAccess, useDidMountEffect, useDisclosure, useIsDemoCompany, usePrimaryInstallation, useReferrerState, type UsePaginationEvent} from 'hooks'
import { useCertificateDeleteMutations, useCertificateMutations, useCertificateBulkActivate, useCertificateArchiveMutations, useConvertDemoCertificate, useCurrentUserCompany, useCertificateGroup, useCertificateGroupUnlink, useDeleteIntroCertificate } from 'hooks/api'
import { Avatar, Box, Stack, Link, Divider, Typography, Tooltip } from '@mui/material'
import { useTheme } from '@mui/material/styles';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro';
import { Button, Image, Modal, Alert, EMPTY_VALUE } from '@repo/ui'
import { isInternalPermission } from '@repo/types'
import {
  Header,
  TransferCertificateModal,
  CancelTransferModal,
  CreateCertificatesModal,
  CreateCertificateDevModal,
  ReplaceTagModal,
  DeleteCertificateModal,
  BulkDeleteCertificatesModal,
  BulkActivationModal,
  SendToLocationModal,
  ConfirmSettingModal,
  ConvertDemoRowModal,
  getConvertCertificateRefusalMessage,
  ActivateCertificateModal,
  CreateCertificateGroupModal,
  SplitGroupModal,
  Table,
  ActionDropdown,
  EmptyState,
  AskAboutButton,
  Loader,
  CertificateCell,
  CertificateOwnerCell,
  BRAND_HELD_QUALIFIER,
  StatusChipCell,
  ProductCell,
  LinkCell,
  getIneligibleReason,
  type ActionDropdownItem,
  IntroDataBanner,
} from 'components'
import {
  SampleSectionClearPrompt,
  useSampleSectionClearPrompt,
} from 'components/feedback/SampleSectionClearPrompt'
import { ExpandAllGroupsToggle, headerWithTerm } from 'components/display/Table'
import { ToggleField } from 'components/input/fields'
import { BadgePlusIcon, TrashIcon, ReplaceIcon, TagIcon, InfoIcon, EyeIcon, ArrowLeftRightIcon, BadgeXIcon, BadgeCheckIcon, FlaskConicalOffIcon, ExternalLinkIcon, ArchiveIcon, XIcon, MergeIcon, SplitIcon, UnlinkIcon, CornerDownRightIcon } from 'lucide-react'
import { ROUTES } from 'routes'

// VRG-231 review round 2 item 12 — a group PARENT is tagless by construction, so
// "Replace tag" never applies to it (used by both the list row kebab and the
// expanded child-panel's own "Parent"-shaped rows — a parent never appears
// inside its own detail panel, but this stays a plain, reusable predicate).
const isGroupParentRow = (row: CertificateAdmin) => Boolean(row.group_divisibility) && !row.parent_certificate_id

// The grouping column's header composes two things — the term tooltip and the
// expand-all control (VRG-231 review round 3) — so the `headerWithTerm` closure is
// built once here rather than rebuilt inside `renderHeader` on every header render.
const CertificateTermHeader = headerWithTerm('Certificate', 'certificate')


const DEFAULT_PAGE_SIZE = 20

// Extract a seeded reference id from the Table's `?filters=field:op:value` param
// (e.g. `product:isAnyOf:<id>` or `owner:isAnyOf:<id>`) — the same format the shared
// Table parser consumes (Table.tsx splits `?filters=` on `&` then `:`). Powers the
// product prefilter (ProductsPage's certificate-count cell) and the owner prefilter
// (Owners tab's certificate-count cell → `/certificates?filters=owner:isAnyOf:<id>`,
// VERA-914 / VRG-105 Stage B).
const parseReferenceFilter = (filterParam: string | null, field: string): string | undefined => {
  if (!filterParam) return undefined
  for (const clause of filterParam.split('&')) {
    const [f, , value] = clause.split(':')
    if (f === field) return value || undefined
  }
  return undefined
}

// VRG-231 Tree Data migration — a group's children used to render inside a row
// Detail Panel (`getDetailPanelContent`), a free-form drawer slotted BELOW a
// row: it structurally cannot carry its own columns, a selection checkbox, or
// align to the header, and it expands via a height swap that read as a flash.
// Migrated to DataGridPro Tree Data (`treeData`/`getTreeDataPath`/
// `groupingColDef` below, wired in `CertificatesPage`'s own `<Table>` JSX) so
// children are REAL rows — every other column renders for them, they get the
// same bulk-select checkbox as any row, and the reveal animates. The
// `CertificateGroupDetailPanel` component this comment used to sit above is
// gone; `getGroupMemberActionItems` (the child-row action set it fed) still
// exists below and now feeds `CertificateActionsCell`'s child branch instead.

// VRG-231 fix G — the list row kebab's "Split group" entry must HIDE (not
// disable) once a group parent has no remaining children (§46, record state:
// a childless parent left behind by an older split has nothing to split, and
// showing the action would open the modal on an empty list with a permanently
// disabled Create button — a dead end). `getActionItems` is a plain per-row
// function (see the column's old `renderCell`), and hooks can't be called
// conditionally inside it, so this thin wrapper does the fetch instead and
// passes the resolved children count down as a second argument.
//
// Module-scope, NOT a closure inside `CertificatesPage` — DataGridPro keys a
// cell renderer's mounted component on its identity, so a function defined
// inside the page component would get a new reference every render and force
// a remount.
//
// Reuses the EXACT SAME query key (`['certificates', 'group', certificateId]`)
// that `GroupMemberCountBadge` (Table/components/Cells/Cells.tsx) already
// fetches for this same row's Certificate-cell member-count badge, so React
// Query dedupes this into that same cached request — no extra network call
// for a parent row that's already showing its badge.
//
// Tree Data migration — a CHILD row (now a real grid row, not a detail-panel
// entry) routes to `getGroupMemberActionItems` instead of `getActionItems`:
// the child action set (Preview / Details / Replace tag / Transfer / Remove
// from group / Delete) differs from a top-level row's (no Activate/Archive/
// Create group/Split group — those stay top-level-row-only actions).
const CertificateActionsCell = ({
  row,
  getActionItems,
  getGroupMemberActionItems,
}: {
  row: CertificateAdmin
  getActionItems: (row: CertificateAdmin, groupHasChildren: boolean, groupIsSplittable: boolean) => ActionDropdownItem[]
  getGroupMemberActionItems: (row: CertificateAdmin) => ActionDropdownItem[]
}) => {
  const isChild = Boolean(row.parent_certificate_id)
  const isGroupParent = Boolean(row.group_divisibility) && !row.parent_certificate_id
  const { data } = useCertificateGroup({ certificateId: row.id }, { enabled: isGroupParent })
  // Non-parent rows never show Split group regardless of this value — but ONLY
  // because that item's `visible` calls `isGroupParentRow(row)`. VRG-231 review
  // round 3: this comment previously claimed the call site "already requires
  // `group_divisibility === 'divisible' && !parent_certificate_id`", which was
  // not true — no such check existed, so the `true` default below was NOT inert
  // and Split group rendered on every standalone certificate. The guard is now
  // real; do not remove it without changing these defaults too.
  const groupHasChildren = !isGroupParent || Boolean(data?.children?.length)
  // VRG-231 review round 2 item 3 — the indivisible-split gate now matches the
  // server (`canSplitCertificateGroup`, utils/certificate.ts): a DIVISIBLE
  // group's Split group is unaffected (the helper returns `true` without
  // waiting on `data`); an INDIVISIBLE group's Split group needs the full
  // membership (`data` — parent + children) to know whether every member is
  // still not-yet-live and unowned.
  const groupIsSplittable = isGroupParent ? canSplitCertificateGroup(row.group_divisibility, data) : true
  return (
    <ActionDropdown
      items={isChild ? getGroupMemberActionItems(row) : getActionItems(row, groupHasChildren, groupIsSplittable)}
    />
  )
}

export const CertificatesPage = () => {
  const navigate = useNavigate()

  // The auto-start that used to live here is gone: the tour is now OFFERED by the
  // "Tour" chip beside the page title (Header → TakeATourChip), on the same
  // per-user "have you taken it" state. Auto-start does not survive a tour library —
  // ten tours would mean ten interruptions, each at the moment the user was trying
  // to do something — so the surfacing is passive and lives in one shared place
  // rather than being wired page by page.
  // Hands "← Back to Certificates" (plus this list's filters/sort) to every detail page linked from here.
  const referrer = useReferrerState()
  const dispatch = useDispatch()
  const theme = useTheme();
  // The brand whose shelf an unsold certificate is still sitting on. This list is
  // company-scoped, so the current company IS that brand; the row's own `company` still
  // wins where the payload carries one.
  const { data: currentCompany } = useCurrentUserCompany()
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: DEFAULT_PAGE_SIZE })
  const [statuses, setStatuses] = useState<CertificateStatus[]>([])
  // Product prefilter (VERA-455): when navigated to as
  // `/certificates?filters=product:isAnyOf:<id>` (e.g. from a product's certificate-count
  // cell on ProductsPage), seed the server-side `productId` from the URL and keep it in
  // sync with the filters drawer. Independent of `statuses`, so a status filter composes
  // on top of the product prefilter rather than replacing it.
  const [searchParams, setSearchParams] = useSearchParams()
  const location = useLocation()
  const [productId, setProductId] = useState<string | undefined>(() =>
    parseReferenceFilter(searchParams.get('filters'), 'product'),
  )
  // Owner prefilter (VERA-914 / VRG-105 Stage B): when navigated to as
  // `/certificates?filters=owner:isAnyOf:<id>` (from the Owners tab's certificate-count
  // cell), seed the server-side `ownerId` from the URL and keep it in sync with the
  // filters drawer — same pattern as `productId` above.
  const [ownerId, setOwnerId] = useState<string | undefined>(() =>
    parseReferenceFilter(searchParams.get('filters'), 'owner'),
  )
  const { devToolsEnabled } = useContext(DevToolsContext) || {}
  
  const [openTransferModal, setOpenTransferModal] = useState(false)
  /**
   * The table selection handed to the transfer modal for a BULK transfer — the raw
   * selection, not the qualifying subset, so the modal can tell the user how many of
   * what they picked will actually move and why the rest will not.
   *
   * Empty for the single-certificate paths (header button, row kebab), which is what
   * the modal switches on.
   */
  const [bulkTransferRows, setBulkTransferRows] = useState<CertificateAdmin[]>([])
  const [openCancelTransferModal, setOpenCancelTransferModal] = useState(false)
  const [openCreateCertificatesModal, setOpenCreateCertificatesModal] = useState(false)

  // Open the Create-certificates modal when arriving with ?create=1 — the sidebar's
  // "Create with phone" entry point, which has no other way to reach a modal owned
  // by this page's local state.
  //
  // `state: location.state` is load-bearing, not defensive: setSearchParams IS a
  // navigation, and a navigation without state replaces the entry's state with
  // undefined, silently discarding any { backTo, backLabel } referrer (§42). Same
  // bug already fixed on RolesPage.
  useEffect(() => {
    if (searchParams.get('create') !== '1') return
    setOpenCreateCertificatesModal(true)
    const next = new URLSearchParams(searchParams)
    next.delete('create')
    setSearchParams(next, { replace: true, state: location.state })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams])
  const [openCreateCertificateDevModal, setOpenCreateCertificateDevModal] = useState(false)
  const [openReplaceTagModal, setOpenReplaceTagModal] = useState(false)
  const [selectedCertificate, setSelectedCertificate] = useState<CertificateAdmin | null>(null)
  const [openDeleteModal, setOpenDeleteModal] = useState(false)
  // VRG-512 — "Remove sample" (AC4). Deliberately its own row/modal state rather than
  // reusing `selectedCertificate`/`openDeleteModal`: those drive `DeleteCertificateModal`,
  // which calls the real certificate-delete use-case (permission-gated, owner/status
  // checks) — a different capability from `DELETE /intro-data/certificates/:id`, which can
  // only ever remove a row already flagged `example_kind = 'intro'`. Sharing state would
  // make it possible to open one modal and confirm into the other's handler.
  const [removeSampleCertificate, setRemoveSampleCertificate] = useState<CertificateAdmin | null>(null)
  /*
   * VRG-836 — "Convert to real". Its OWN staged row, for the same reason the sample-removal
   * state above has its own: this fires a billable conversion, and sharing state with any
   * other modal would make it possible to open one and confirm into this handler.
   *
   * GATED BEFORE APPLYING (§43) — staging the row is all the kebab does; nothing is called
   * until the modal's confirm, so there is no optimistic badge flip for a cancel to revert.
   */
  const [convertDemoCertificate, setConvertDemoCertificate] = useState<CertificateAdmin | null>(null)
  const [openBulkDeleteModal, setOpenBulkDeleteModal] = useState(false)
  const [openBulkActivateModal, setOpenBulkActivateModal] = useState(false)
  const [openActivateModal, setOpenActivateModal] = useState(false)
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  // VRG-231 fix B — the GRID's own row-selection state (checkboxes + the bulk
  // bar's open/closed state), CONTROLLED from here so a successful bulk action
  // can actually clear it. `selectedIds` above stays a SEPARATE state: several
  // handlers (handleBulkActivate, handleBulkDelete) narrow it down to just the
  // eligible subset before opening a confirm modal, and that narrowing must
  // not also retroactively uncheck rows in the grid out from under the user —
  // if the two shared one state, clicking "Activate" in the bulk bar would
  // visibly un-tick the ineligible rows the instant the confirm modal opens.
  // `Table`'s own `onSelectionModelChange` callback (passed below) keeps
  // `selectedIds` in sync exactly as before; this is purely additive.
  const [gridSelectionModel, setGridSelectionModel] = useState<(string | number)[]>([])
  const [pendingStatusAction, setPendingStatusAction] = useState<'activate' | null>(null)
  const [openArchiveModal, setOpenArchiveModal] = useState(false)
  // VRG-231 review item 7 — "Create group" entry point (list row kebab). See the
  // matching header-overflow entry on CertificatePage.tsx for the full rationale.
  const [openCreateGroupModal, setOpenCreateGroupModal] = useState(false)
  // VRG-231/VRG-337 item C — the bulk-bar "Group certificates" entry point's own
  // seed, separate from `selectedCertificate` (the single-row kebab path's state,
  // left untouched). Passed to the modal's `initialCertificates` (plural) prop.
  const [groupModalInitialCertificates, setGroupModalInitialCertificates] = useState<CertificateAdmin[] | null>(
    null,
  )
  // VRG-231 (add-to-existing-group) — set alongside `groupModalInitialCertificates`
  // when the bulk selection already includes exactly one existing group's parent:
  // the modal opens in "add to this group" mode instead of "create a new group".
  const [groupModalExistingParent, setGroupModalExistingParent] = useState<CertificateAdmin | null>(null)
  // VRG-231 review round 2 item 11 — "Split group" entry point (list row kebab),
  // matching the certificate detail page's own header action.
  const [openSplitGroupModal, setOpenSplitGroupModal] = useState(false)
  // VRG-231 fix E — a CHILD's own "Remove from group" (renamed from "Split from
  // group", fix D) used to unlink immediately on click with no confirmation at
  // all. `unlink-certificates-from-group.use-case.ts` auto-expands to a FULL
  // group dissolve when removing this member would leave exactly one behind
  // ("a group of 1 is not a group") — correct server behavior, but it surprised
  // the user (removing one of two members silently removed both). This state
  // drives a confirm modal that discloses the dissolve BEFORE the click commits.
  const [removeFromGroupTarget, setRemoveFromGroupTarget] = useState<CertificateAdmin | null>(null)
  const [openRemoveFromGroupModal, setOpenRemoveFromGroupModal] = useState(false)
  const [showArchived, setShowArchived] = useState(false)
  // Draft value for the Filters panel — committed to showArchived only on Apply.
  const [showArchivedDraft, setShowArchivedDraft] = useState(false)

  const [searchValue, setSearchValue] = useState<string>('')
  // VRG-265, corrected — the scan filter is no longer a list of its own. Scanning routes into
  // the Filters drawer's Certificate field, which stages `isAnyOf` ids like any other checked
  // option; `handleFiltersChange` maps that field to this param. So there is ONE list (the
  // filter's) and one way to see or undo it (the field's own checkboxes and Clear).
  const [certificateIds, setCertificateIds] = useState<string[]>([])
  // START SCAN_SESSION MODAL
  const [openScanSessionQRCodeModal, setOpenScanSessionQRCOdeModal] = useState(false)
  const [scanSessionRegisterURL, setScanSessionRegisterURL] = useState('')
  // END SCAN_SESSION MODAL
  const [scanSessionDeviceRegistered, setScanSessionDeviceRegistered] = useState(false)
  const { open: notification } = useSnackbar()

  const { data: primaryInstallation, isLoading: isPrimaryLoading, isError: isPrimaryError, refetch: refetchPrimaryInstallation } = usePrimaryInstallation()

  // VRG-512 — the first-real-certificate offer to clear this page's samples. The hook is
  // inert until `notifyRealRecordCreated` is called AND its own conditions hold.
  const samplePrompt = useSampleSectionClearPrompt('certificates')

  const { data, isLoading, refetch } = useCertificates({
    companyId: DEFAULT_COMPANY_ID, // TODO: replace with real company id
    limit: paginationModel.pageSize,
    offset: paginationModel.page * paginationModel.pageSize,
    statuses: statuses.length ? statuses : undefined,
    query: searchValue || undefined,
    // Omitted, never `[]` — an empty array is a filter that matches nothing.
    certificateIds: certificateIds.length ? certificateIds : undefined,
    include_archived: showArchived || undefined,
    productId,
    ownerId,
  })

  // VRG-227 — Download used to export only the loaded page. Re-issues the SAME query
  // params `useCertificates` above already drives (filters + search), just with a
  // different limit/offset, so Table can walk every matching row for export. Called
  // directly via `createApiCall` rather than `useCertificates`'s redux action — that hook
  // writes into `state.certificates.certificates`, a single shared slice, so dispatching an
  // export page through it would clobber the on-screen page's rows with the export's
  // batches mid-fetch.
  const fetchExportPage = useCallback(async ({ limit, offset }: { limit: number; offset: number }) => {
    const response = await createApiCall({
      url: '/certificates/admin',
      params: {
        companyId: DEFAULT_COMPANY_ID,
        statuses: statuses.length ? statuses : undefined,
        query: searchValue || undefined,
        certificateIds: certificateIds.length ? certificateIds : undefined,
        include_archived: showArchived || undefined,
        productId,
        ownerId,
        limit,
        offset,
      },
    })
    const body = response.data as { data: CertificateAdmin[]; total: number }
    return { rows: body?.data ?? [], total: body?.total ?? 0 }
  }, [statuses, searchValue, certificateIds, showArchived, productId, ownerId])

  // Bulk activate mutation (server-side)
  const { bulkActivate, isLoading: isBulkActivating } = useCertificateBulkActivate({
    onSuccess: (data) => {
      const activated = data?.summary?.activated ?? 0
      const skipped = data?.summary?.skipped ?? 0
      const failed = data?.summary?.failed ?? 0
      if (activated > 0) {
        notification({
          type: 'success',
          title: `${activated} certificate${activated > 1 ? 's' : ''} activated successfully`,
        })
      }
      if (skipped > 0 || failed > 0) {
        notification({
          type: 'warning',
          title: `${skipped + failed} certificate${skipped + failed > 1 ? 's' : ''} could not be activated`,
        })
      }
      setOpenBulkActivateModal(false)
      setSelectedIds([])
      // VRG-231 fix B — clear the GRID's own selection too, not just the
      // modal-target-ids state. See `gridSelectionModel`'s doc for why they're
      // two separate states.
      setGridSelectionModel([])
      refetch()
    },
    onError: (error) => {
      const message = getErrorMessage(error)
      notification({ type: 'error', title: message })
    },
  })

  // Bulk delete mutation (TanStack Query)
  const [{ bulkRemove }, { isLoading: isBulkDeleting }] = useCertificateDeleteMutations({
    onSuccess: () => {
      setOpenBulkDeleteModal(false)
      setSelectedIds([])
      // VRG-231 fix B — see the bulk-activate onSuccess above.
      setGridSelectionModel([])
      refetch()
    },
    onError: (error) => {
      const message = getErrorMessage(error)
      notification({ type: 'error', title: message })
    },
  })

  // VRG-512 — "Remove sample" (AC4). `deletingCertificateId` gives category-3 (in-flight,
  // §46) per-ROW disable: only the sample actually being deleted shows a busy kebab item,
  // not every sample row on the page.
  const {
    deleteIntroCertificateAsync,
    isDeletingIntroCertificate,
    deletingCertificateId,
    deleteIntroCertificateError,
  } = useDeleteIntroCertificate()

  const handleRemoveSampleCertificate = (row: CertificateAdmin) => {
    setRemoveSampleCertificate(row)
  }

  /*
   * VRG-836 — convert ONE demo certificate to real. ⚠️ BILLABLE: the server clears
   * `example_kind` AND records the activation, so it lands on the next invoice.
   *
   * `convertingCertificateId` gives the same per-ROW in-flight disable the removal above
   * uses (§46 category 3) — only the row actually converting shows a busy item.
   */
  const isDemoCompany = useIsDemoCompany()
  const {
    convertDemoCertificateAsync,
    isConvertingDemoCertificate,
    convertingCertificateId,
    convertDemoCertificateError,
  } = useConvertDemoCertificate()

  const handleConvertDemoCertificate = (row: CertificateAdmin) => {
    setConvertDemoCertificate(row)
  }

  const handleConfirmConvertDemoCertificate = async () => {
    if (!convertDemoCertificate) return
    try {
      const result = await convertDemoCertificateAsync(convertDemoCertificate.id)
      /*
       * `converted: true, metered: false` is a FREE REAL CERTIFICATE — the server's own
       * docblock calls it the one result a caller must surface as a fault rather than as
       * success. Every known cause refuses before the flag is cleared, so reaching here
       * means the cause is new and a human needs to know. Surfaced as an error toast and
       * the modal is left open, because silently closing would report success.
       */
      if (result.converted && !result.metered) {
        notification({
          type: 'error',
          title:
            'This certificate was converted but the activation was not recorded. ' +
            'Contact support before relying on it.',
        })
        return
      }
      if (!result.converted) {
        notification({type: 'error', title: getConvertCertificateRefusalMessage(result.refusal)})
        return
      }
      setConvertDemoCertificate(null)
      // Visible-action success — the Demo badge disappears from the refetched row — so no
      // success toast (toast-usage-policy). Failure is not self-evident and stays inline.
    } catch {
      // Left open deliberately: closing on failure would read as success.
    }
  }

  const handleConfirmRemoveSampleCertificate = async () => {
    if (!removeSampleCertificate) return
    try {
      await deleteIntroCertificateAsync(removeSampleCertificate.id)
      setRemoveSampleCertificate(null)
      // Visible-action success (the row disappears from the refetched table) is its own
      // feedback — no success toast (toast-usage-policy). Failure is not self-evident, so
      // it stays inline in the modal via `deleteIntroCertificateError` below, same pattern
      // as `ConfirmSettingModal`'s own `children` slot.
    } catch {
      // Leave the modal open on failure — same reasoning as IntroDataBanner's
      // handleConfirm: closing it would read as success.
    }
  }

  // Activate mutations
  const [{ activate: activateCertificateStatus }, mutationState] = useCertificateMutations({
    onSuccess: () => {
      if (pendingStatusAction !== 'activate') return

      setPendingStatusAction(null)
      refetch()
    },
    onError: (error) => {
      setPendingStatusAction(null)
      const message = getErrorMessage(error)
      notification({ type: 'error', title: message })
    },
  })

  const { isHasAccess: isCanTransfer } = useCanAccess({ action: 'transfer_certificate' })
  // VERA-931 / VRG-528 — confirming arrival has its OWN permission. `transfer_certificate`
  // gates DISPATCH ("Send to location"); receipt is the billable moment and is done by
  // shop/logistics staff, so it gates separately. Must match CertificatePage's row kebab
  // exactly — the two menus offering different gates for the same action is the drift this
  // pair is checked against.
  const { isHasAccess: isCanReceiveCustody } = useCanAccess({ action: 'custody_receive' })
  const { isHasAccess: isCanManageGroups } = useCanAccess({ action: 'manage_certificate_groups' })

  // VRG-231 review round 2 item 11 — group data for the row targeted by the list's
  // own "Split group" entry (fetched only while that modal is open; React Query
  // dedupes against the same query key the row's own detail-panel/count badge use).
  const { data: splitGroupData } = useCertificateGroup(
    { certificateId: selectedCertificate?.id as string },
    { enabled: openSplitGroupModal && Boolean(selectedCertificate?.id) },
  )

  const handleOpenSplitGroupModal = (row: CertificateAdmin) => {
    setSelectedCertificate(row)
    setOpenSplitGroupModal(true)
  }

  const handleCloseSplitGroupModal = () => {
    setOpenSplitGroupModal(false)
    setSelectedCertificate(null)
  }

  const handleSplitGroupSuccess = () => {
    refetch()
  }

  // VRG-231 fix D — a CHILD's own "Remove from group" (renamed from "Split
  // from group": on a child it removes ONE member, "split" implied dissolving
  // the whole thing — see CertificatePage's identical handler for the full
  // rationale) reuses the same unlink endpoint as the header/list "Split
  // group" entry.
  const { unlinkCertificateGroup, isUnlinking } = useCertificateGroupUnlink({
    onSuccess: () => {
      notification({ type: 'success', title: 'Removed from group' })
      handleCloseRemoveFromGroupModal()
      refetch()
    },
    onError: (error) => {
      notification({ type: 'error', title: getErrorMessage(error) })
    },
  })

  // VRG-231 fix E — see `removeFromGroupTarget`'s doc: name the dissolve
  // BEFORE the click commits, not after. A group of exactly 2 dissolves
  // entirely when either member is removed (server-side, see
  // unlink-certificates-from-group.use-case.ts) — this reads that same
  // condition off the already-fetched `childrenByParentId` map (declared
  // above), no extra fetch.
  const handleOpenRemoveFromGroupModal = (row: CertificateAdmin) => {
    setRemoveFromGroupTarget(row)
    setOpenRemoveFromGroupModal(true)
  }

  const handleCloseRemoveFromGroupModal = () => {
    setOpenRemoveFromGroupModal(false)
    setRemoveFromGroupTarget(null)
  }

  const handleConfirmRemoveFromGroup = () => {
    if (!removeFromGroupTarget) return
    unlinkCertificateGroup({ certificateId: removeFromGroupTarget.id })
  }
  // VRG-268 — see CertificatePage: the dedicated permission for repointing a certificate at a
  // new physical tag, mirroring the API's `@Permissions(CertificateReplaceTag)` guard.
  const { isHasAccess: canReplaceTag } = useCanAccess({ action: 'certificate_replace_tag' })
  // §24 (working-conventions): Activate and Delete are their own permissions, not
  // folded into `manage_certificate` — the API's guards agree
  // (certificates.admin.controller.ts activate/bulk-activate use
  // @Permissions(PermissionName.CertificateActivate); delete/bulk-delete use
  // @Permissions(PermissionName.CertificateDelete)). Both are platform_admin ONLY
  // for now — a deliberate, explicitly provisional product decision (VERA-455).
  const { isHasAccess: canActivateCertificate } = useCanAccess({ action: 'certificate_activate' })
  const { isHasAccess: canDeleteCertificate } = useCanAccess({ action: 'certificate_delete' })
  // VRG-273 — see CertificatePage: `certificate_delete` is internal-scoped and the write-side
  // allowlist rejects granting it to any company role, so §27 says HIDE (not disable+tooltip)
  // for non-internal users. `isInternalPermission` is read generically so the next
  // internal-scoped permission gets the same hide-treatment for free.
  const { isHasAccess: isInternalUser } = useCanAccess({ role: 'platform_admin' })

  // Server-side pagination: convert MUI's { page, pageSize } to the API's limit/offset.
  const handlePaginationModelChange = (newModel: { page: number; pageSize: number }) => {
    // Reset to the first page when the page size changes.
    if (newModel.pageSize !== paginationModel.pageSize) {
      setPaginationModel({ page: 0, pageSize: newModel.pageSize })
    } else {
      setPaginationModel(newModel)
    }
  }

  // Server-side search: reset to the first page on a new query.
  const handleSearchChange = (value: string) => {
    setSearchValue(value)
    setPaginationModel(prev => ({ ...prev, page: 0 }))
  }

  // Server-side status filtering from the filters drawer (VERA-438). The drawer emits
  // an array of { field, operator, value }; map the 'status' filter to the statuses query.
  const handleFiltersChange = (appliedFilters: { field: string; value: unknown }[]) => {
    const statusFilter = appliedFilters.find(f => f.field === 'status')
    // Expand before querying: the drawer hands back the option's own value, which
    // for "Transfer" represents three statuses.
    setStatuses(
      expandStatusFilter((statusFilter?.value as string[]) || []) as CertificateStatus[],
    )
    // Keep the product prefilter in sync with the drawer: the Table reports the full
    // applied set (the same invariant the status filter relies on), so an absent product
    // filter means it was cleared. `value` may be a scalar or an isAnyOf array.
    const productFilter = appliedFilters.find(f => f.field === 'product')
    const productValue = Array.isArray(productFilter?.value)
      ? productFilter?.value[0]
      : productFilter?.value
    setProductId((productValue as string | undefined) || undefined)
    // Same invariant for the owner prefilter (VERA-914 / VRG-105 Stage B).
    const ownerFilter = appliedFilters.find(f => f.field === 'owner')
    const ownerValue = Array.isArray(ownerFilter?.value)
      ? ownerFilter?.value[0]
      : ownerFilter?.value
    setOwnerId((ownerValue as string | undefined) || undefined)
    // VRG-265 — the Certificate and ID columns both name a certificate, and both are scan
    // targets. Their `isAnyOf` values are certificate ids, which the list takes as
    // `certificateIds`. This is what makes a scanned tag narrow the list: it is an ordinary
    // filter value, not a parallel mechanism.
    const certFilter = appliedFilters.filter(f => f.field === 'certificate' || f.field === 'id')
    const certValues = certFilter.flatMap(f =>
      (Array.isArray(f.value) ? f.value : [f.value]).filter(Boolean),
    ) as string[]
    setCertificateIds(certValues)
    setPaginationModel(prev => ({ ...prev, page: 0 }))
  }

  const allCertificates = (data?.data as CertificateAdmin[]) || []
  // VRG-236/VRG-231 — a group's children must not ALSO render as their own
  // top-level rows; they're reachable only via their parent's Tree Data
  // expansion (see the `treeRows`/`getTreeDataPath` block below). The API's
  // `getAggregatedList`/`getAggregatedTotal` already filter server-side
  // (`topLevelOnly: true`, see get-certificates-admin-list.use-case.ts), so
  // `total`/pagination are authoritative there — this client-side filter is
  // now defense-in-depth, not the primary mechanism.
  const certificates = useMemo(
    () => allCertificates.filter(c => !c.parent_certificate_id),
    [allCertificates],
  )
  const total = data?.total

  // VRG-231 Tree Data — fetch every ON-SCREEN group parent's children so they
  // can be merged into the grid's own row array as REAL rows (see the
  // `treeData` prop doc on Table.tsx for why: a Detail Panel cannot give a
  // child a column set, a selection checkbox, or an animated reveal; a tree
  // row can). `useQueries` (not `useCertificateGroup` in a loop — hooks can't
  // be called a variable number of times) lets the query COUNT vary per page
  // (0–N parents), while using the EXACT SAME `['certificates', 'group', id]`
  // query key `useCertificateGroup` itself uses (see BulkActivationModal for
  // the same pattern) — so this dedupes against any other cell on the same
  // page already fetching the same group (the certificate cell's own member-
  // count badge, `CertificateActionsCell`'s "does this parent still have
  // children" check, etc.) rather than issuing a second request.
  const groupParentIds = useMemo(
    () => certificates.filter(isGroupParentRow).map(c => c.id),
    [certificates],
  )
  const groupQueries = useQueries({
    queries: groupParentIds.map((certificateId) => ({
      queryKey: ['certificates', 'group', certificateId],
      queryFn: async () => {
        const response = await createApiCall({
          method: 'GET',
          url: `/certificates/admin/groups/${certificateId}`,
        })
        if (response.status !== 200) {
          throw new Error(`Failed to fetch certificate group ${certificateId}`)
        }
        return response.data as CertificateGroupData
      },
      enabled: Boolean(certificateId),
    })),
  })
  // VRG-231 review round 2 item 2 — `useQueries` hands back a NEW array wrapper
  // on every render of this component, even when none of the underlying group
  // data actually changed (query-core only reuses the previous array when a
  // `combine` option is passed, which this call doesn't use). Depending on that
  // array's IDENTITY (as this used to) meant `childrenByParentId` — and the
  // `treeRows` memo below it — recomputed into a BRAND-NEW array on every
  // re-render of this page, including one triggered by nothing more than
  // ticking a row's selection checkbox. DataGridPro treats a new `rows` array
  // reference as a full row-tree rebuild, which recreates every group node from
  // scratch and resets `childrenExpanded` to the tree's default (collapsed) —
  // this is what made selecting ANY row (a child's checkbox was the reported
  // case, but a parent's does the same) silently collapse an expanded parent.
  // `dataUpdatedAt` is a primitive per query that changes ONLY when that
  // query's data is actually refetched, so joining it into one string gives
  // `childrenByParentId` a dependency that stays referentially stable across
  // renders where nothing group-related changed — which keeps `treeRows`
  // stable too, so DataGridPro never mistakes an unrelated re-render for new
  // data and never resets expansion state.
  const groupQueriesVersion = groupQueries.map(q => q.dataUpdatedAt).join(',')
  // Keyed by parent id so the "will removing this child dissolve the whole
  // group" check (fix E, below) can look up a specific child's siblings
  // without re-deriving the whole tree.
  const childrenByParentId = useMemo(() => {
    const map = new Map<string, CertificateAdmin[]>()
    groupParentIds.forEach((id, i) => {
      const children = groupQueries[i]?.data?.children
      if (children?.length) map.set(id, children)
    })
    return map
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `groupQueriesVersion`
    // is the intentional, referentially-stable stand-in for `groupQueries` (see
    // the comment above); depending on `groupQueries` itself defeats the fix.
  }, [groupParentIds, groupQueriesVersion])
  // VRG-231 review round 2 item 3 — a lookup from a group's PARENT id to that
  // parent's own row (status/owner), so a CHILD row's own "Remove from group"
  // (`getGroupMemberActionItems` below) can resolve `canSplitCertificateGroup`
  // for an INDIVISIBLE group without an extra fetch: every parent currently
  // rendering a child is, by construction, also a top-level row in `certificates`.
  const certificatesById = useMemo(
    () => new Map(certificates.map(c => [c.id, c])),
    [certificates],
  )

  // The grid's FULL row set: every top-level row plus every fetched child, so
  // DataGridPro's own tree-building (`getTreeDataPath` below) can place each
  // child under its parent. A child not yet fetched (query still in flight)
  // simply isn't in `rows` yet, so its parent renders with no expand toggle
  // until it arrives — no placeholder row, no error state, just a brief
  // moment where the parent looks childless.
  const treeRows = useMemo(
    () => [...certificates, ...Array.from(childrenByParentId.values()).flat()],
    [certificates, childrenByParentId],
  )
  // Top-level row -> its own id is the whole path; a child -> [parent id, own
  // id]. One level only (a group has a parent and children, never
  // grandchildren — enforced server-side, see link-certificates-to-group.use-
  // case.ts's "ONE LEVEL ONLY" checks), so no path is ever longer than 2.
  const getTreeDataPath = useCallback(
    (row: CertificateAdmin) =>
      row.parent_certificate_id ? [row.parent_certificate_id, row.id] : [row.id],
    [],
  )

  // VRG-236 — chevron + inline expand only for a group's tagless PARENT (its own
  // `parent_certificate_id` is null, but it carries `group_divisibility`).
  // Returning `null` for every other row means DataGridPro renders no chevron
  // for them at all (see Table.tsx). On the MUI Community fallback (no
  // `hasMuiXLicense`) `treeData` is inert — DataGridCommunity ignores it, and
  // Table trims `rows` back down to top-level-only there — so no chevron
  // appears for anyone; `GroupMemberCountBadge` inside `CertificateCell` still
  // shows the member count in both cases.
  //
  // Selection decision (REVISED — Tree Data migration): a child row now gets
  // its OWN bulk-select checkbox like any other row (fix 5/B), since it's a
  // real entry in `rows` with its own id. Bulk actions (Activate/Transfer/
  // Delete/Group) therefore act on WHATEVER is checked, parent or child alike
  // — each action's own per-row eligibility rule (status, ownership,
  // `getIneligibleReason`) already governs whether a given row can actually
  // participate, the same as it does for any other row.
  //
  // VRG-231 review round 2 item 11 — moved below `getGroupMemberActionItems`
  // (declared further down this component) so this callback can close over it
  // without a temporal-dead-zone reference error in its dependency array.

  // Distinguishes "no certificates exist yet" from "a filter narrowed the list to
  // zero" (mirrors PeoplePage's `hasActiveFilters`, VRG-227) — the two need different
  // empty-state copy and only the latter is a good moment to offer Ask Vera.
  // `showArchived` is a "show more" toggle, not a narrowing filter, so it is
  // deliberately excluded here.
  const hasActiveFilters = Boolean(
    searchValue || statuses.length || productId || ownerId || certificateIds.length,
  )

  //UPDATE FOR 
  const { data: lastImport, isLoading: isLastImportLoading } = useProductsLastImport()

  // TODO: Funcs above are from before the push

  const handleViewCertificate = (id: string) => {
    navigate(id, { state: referrer })
  }

  const handleTransferCertificate = (row: CertificateAdmin) => {
    // Prevent transferring certificates already in transfer state
    if (isCertificateInTransferState(row.status)) {
      const message = getTransferDisabledMessage(row.status) || 'This certificate is already being transferred'
      notification({
        type: 'error',
        title: 'Cannot transfer certificate',
        text: message,
      })
      return
    }
    
    // Clear any prior bulk selection — otherwise a bulk transfer followed by a row
    // transfer would reopen the modal still in batch mode.
    setBulkTransferRows([])
    setSelectedCertificate(row)
    setOpenTransferModal(true)
  }

  /**
   * Bulk transfer: N certificates -> ONE recipient.
   *
   * Hands the modal the WHOLE selection rather than pre-filtering to the qualifying
   * rows, so it can state "3 of the 5 you picked" and name the reason for the other
   * two. Filtering here would make the exclusions invisible, which is the failure
   * this replaced: the action used to fire a snackbar telling the user to transfer
   * certificates one at a time while being offered as a bulk action.
   */
  const handleBulkTransfer = (selectedRows: CertificateAdmin[]) => {
    setSelectedCertificate(null)
    setBulkTransferRows(selectedRows)
    setOpenTransferModal(true)
  }

  const handleDeleteCertificate = (row: CertificateAdmin) => {
    const hasOwner = row.customer_id || row.customer

    if (hasOwner) {
      notification({
        type: 'error',
        title: 'Cannot delete certificate',
        text: 'Certificates with owners cannot be deleted'
      })
      return
    }

    if (row.status !== 'inactive') {
      notification({
        type: 'error',
        title: 'Cannot delete certificate',
        text: 'Only inactive certificates can be deleted'
      })
      return
    }

    setSelectedCertificate(row)
    setOpenDeleteModal(true)
  }

  const handleCloseTransferModal = () => {
    setSelectedCertificate(null)
    setBulkTransferRows([])
    setOpenTransferModal(false)
  }

  const handleCancelTransferCertificate = (row: CertificateAdmin) => {
    setOpenCancelTransferModal(true)
    setSelectedCertificate(row)
  }

  const handleCloseCancelTransferModal = () => {
    setOpenCancelTransferModal(false)
    setSelectedCertificate(null)
  }

  const handleReplaceTagCertificate = (row: CertificateAdmin) => {
    setOpenReplaceTagModal(true)
    setSelectedCertificate(row)
  }

  const handleCloseReplaceTagModal = () => {
    setOpenReplaceTagModal(false)
    setSelectedCertificate(null)
  }

  const handleCloseDeleteModal = () => {
    setOpenDeleteModal(false)
    setSelectedCertificate(null)
  }

  const handleBulkDelete = (selectedRows: CertificateAdmin[]) => {
    const deletableRows = selectedRows.filter(isDeleteAvailable)
    const nonDeletableRows = selectedRows.filter(row => row.status !== 'inactive' || row.customer_id || row.customer)

    if (nonDeletableRows.length > 0) {
      notification({
        type: 'warning',
        title: `${nonDeletableRows.length} certificate(s) cannot be deleted`,
        text: 'Certificates with owners or non-inactive status cannot be deleted'
      })
    }

    if (deletableRows.length > 0) {
      // Update selectedIds to only include deletable certificates
      setSelectedIds(deletableRows.map(row => row.id))
      setOpenBulkDeleteModal(true)
    }
  }

  const handleCloseBulkDeleteModal = () => {
    setOpenBulkDeleteModal(false)
  }

  const handleCreateCertificates = (row: CertificateAdmin) => {
    setOpenCreateCertificatesModal(true)
  }

  const handleCloseCreateCertificatesModal = () => {
    setOpenCreateCertificatesModal(false)
  }

  const handleCreateCertificateDev = () => {
    setOpenCreateCertificateDevModal(true)
  }

  const handleCloseCreateCertificateDevModal = () => {
    setOpenCreateCertificateDevModal(false)
  }

  /**
   * VRG-512 — what both create modals call on success, instead of bare `refetch`.
   *
   * Order matters, and not for the reason it looks like. `notifyRealRecordCreated` reads
   * the "did this tenant have real certificates BEFORE" bit from a ref holding the last
   * RENDERED value, so it is unaffected by `refetch` invalidating the cache — but calling
   * it first keeps that independence obvious rather than incidental. The hook itself
   * decides whether to open anything; from here this is unconditional.
   */
  const handleCertificatesCreated = () => {
    samplePrompt.notifyRealRecordCreated()
    refetch()
  }

  const handlePreviewCertificate = async ({row}) => {
    // If still loading, try to wait for it or refetch
    if (isPrimaryLoading || !primaryInstallation) {
      try {
        const { data } = await refetchPrimaryInstallation()
        if (!data?.installation_url) {
          notification({
            type: 'error',
            title: 'Primary installation not found',
          })
          return
        }
        const baseUrl = data.installation_url
        const separator = baseUrl.includes('?') ? '&' : '?'
        const url = `${baseUrl}${separator}certificate_id=${encodeURIComponent(row.id)}`
        window.open(url, '_blank', 'noopener,noreferrer')
        return
      } catch (error) {
        notification({
          type: 'error',
          title: 'Failed to load primary installation',
        })
        return
      }
    }

    if (isPrimaryError || !primaryInstallation?.installation_url) {
      notification({
        type: 'error',
        title: 'Primary installation not found',
      })
      return
    }

    const baseUrl = primaryInstallation.installation_url
    const separator = baseUrl.includes('?') ? '&' : '?'
    const url = `${baseUrl}${separator}certificate_id=${encodeURIComponent(row.id)}`

    window.open(url, '_blank', 'noopener,noreferrer')
  }

  const handleActivateCertificate = (row: CertificateAdmin) => {
    setSelectedCertificate(row)
    setOpenActivateModal(true)
  }

  const handleCloseActivateModal = () => {
    setOpenActivateModal(false)
    setSelectedCertificate(null)
  }

  const handleConfirmActivate = () => {
    if (selectedCertificate) {
      setPendingStatusAction('activate')
      activateCertificateStatus({ certificateId: selectedCertificate.id })
      setOpenActivateModal(false)
    }
  }

  const handleBulkActivate = (selectedRows: CertificateAdmin[]) => {
    const activatableRows = selectedRows.filter(row => isActivateAvailable(row.status))

    if (activatableRows.length === 0) {
      notification({
        type: 'warning',
        title: 'No certificates can be activated',
        text: 'Only inactive or created certificates can be activated'
      })
      return
    }

    // Update selectedIds to only include activatable certificates, then open modal
    setSelectedIds(activatableRows.map(row => row.id))
    setOpenBulkActivateModal(true)
  }

  const handleCloseBulkActivateModal = () => {
    setOpenBulkActivateModal(false)
  }

  const { archiveCertificate, isArchiving } = useCertificateArchiveMutations({
    onSuccess: () => {
      setOpenArchiveModal(false)
      setSelectedCertificate(null)
      refetch()
    },
    onError: (error) => {
      const message = getErrorMessage(error)
      notification({ type: 'error', title: message })
    },
  })

  const handleArchiveCertificate = (row: CertificateAdmin) => {
    setSelectedCertificate(row)
    setOpenArchiveModal(true)
  }

  const handleCloseArchiveModal = () => {
    setOpenArchiveModal(false)
    setSelectedCertificate(null)
  }

  // VRG-231 review item 7 — "Create group" row-kebab entry point. See
  // CreateCertificateGroupModal for the full "group existing certificates" flow
  // (VRG-337).
  const handleOpenCreateGroupModal = (row: CertificateAdmin) => {
    setSelectedCertificate(row)
    setOpenCreateGroupModal(true)
  }

  // VRG-231 item C — bulk-bar "Group certificates" entry point. Filters out
  // ineligible rows (already grouped, or owned/previously-owned — mirrors the
  // modal's own `getIneligibleReason`, which is exported specifically so this
  // stays in sync with it) and reports the drop before opening the modal,
  // rather than silently including rows the create call would reject (§46:
  // the bulk bar itself doesn't hide/disable per-row — the handler explains
  // after the click, matching handleBulkActivate/handleBulkDelete above).
  //
  // Smarter grouping (approved design): if the selection already includes an
  // EXISTING group's parent, "Group certificates" means adding the other
  // selected certificates to THAT group, not standing up a second, unrelated
  // one. Detected here, before the general eligibility filter below (which
  // would otherwise just drop the parent under the generic "already grouped"
  // reason and silently create an unrelated new group from the rest —
  // technically not wrong, but not what the user asked for either).
  //
  // Wired end to end (VRG-231 add-to-existing-group): the API route is
  // `POST certificates/admin/groups/:certificateId/link`
  // (LinkCertificatesToGroupUseCase), and CreateCertificateGroupModal's
  // `existingParent` prop switches it into "add to this group" mode (fixed
  // parent, inherited/hidden divisibility, no parent-product picker).
  const handleOpenCreateGroupModalBulk = (selectedRows: CertificateAdmin[]) => {
    const existingParents = selectedRows.filter(isGroupParentRow)

    if (existingParents.length > 1) {
      notification({
        type: 'error',
        title: "Can't group — multiple existing groups selected",
        text: 'Your selection includes more than one certificate group. Remove all but one before grouping.',
      })
      return
    }

    if (existingParents.length === 1) {
      const existingParent = existingParents[0]
      // The rest of the selection, minus the parent itself, filtered for the
      // same eligibility rule as the create-new-group path below (already
      // grouped, or owned/previously-owned rows can't be added either).
      const otherRows = selectedRows.filter(row => row.id !== existingParent.id)
      const eligibleRows = otherRows.filter(row => !getIneligibleReason(row))
      const ineligibleCount = otherRows.length - eligibleRows.length

      if (ineligibleCount > 0) {
        notification({
          type: 'warning',
          title: `${ineligibleCount} certificate${ineligibleCount > 1 ? 's' : ''} can't be added`,
          text: 'Certificates already in a group, or with an owner (or ownership history), cannot be added to a group',
        })
      }

      if (eligibleRows.length < 1) {
        notification({
          type: 'warning',
          title: 'No eligible certificates to add',
          text: 'Select at least 1 other eligible certificate to add to this group',
        })
        return
      }

      setGroupModalExistingParent(existingParent)
      setGroupModalInitialCertificates(eligibleRows)
      setOpenCreateGroupModal(true)
      return
    }

    const eligibleRows = selectedRows.filter(row => !getIneligibleReason(row))
    const ineligibleCount = selectedRows.length - eligibleRows.length

    if (ineligibleCount > 0) {
      // `text`, not `message` — the latter isn't a real `NotificationProps` field
      // (a pre-existing mismatch elsewhere in this file, out of scope to sweep
      // here; new code uses the field the type actually declares).
      notification({
        type: 'warning',
        title: `${ineligibleCount} certificate${ineligibleCount > 1 ? 's' : ''} can't be grouped`,
        text: 'Certificates already in a group, or with an owner (or ownership history), cannot be grouped',
      })
    }

    if (eligibleRows.length < 2) {
      notification({
        type: 'warning',
        title: 'Not enough eligible certificates to group',
        text: 'At least 2 eligible certificates are required to create a group',
      })
      return
    }

    setGroupModalInitialCertificates(eligibleRows)
    setOpenCreateGroupModal(true)
  }

  const handleCloseCreateGroupModal = () => {
    setOpenCreateGroupModal(false)
    setSelectedCertificate(null)
    setGroupModalInitialCertificates(null)
    setGroupModalExistingParent(null)
  }

  // VRG-231 fix B — the bulk bar stayed open reading "N selected" after a
  // successful group creation because nothing told the GRID's own selection
  // state to clear (see `gridSelectionModel`'s doc). `refetch` alone (the
  // previous `onCreated`) only refreshes the row data.
  const handleGroupCreated = () => {
    refetch()
    setSelectedIds([])
    setGridSelectionModel([])
  }

  const handleConfirmArchive = () => {
    if (selectedCertificate) {
      archiveCertificate(selectedCertificate.id)
    }
  }

  /*
    VERA-931 — the custody hop, offered from the LIST as well as the detail page.
    Sending stock to a location is a bulk-ish, list-shaped job (a shipment is many
    certificates), so making the operator open each certificate to do it would be
    the wrong shape. `useOpenCustodyHops` is the company's open set, so the per-row
    lookup is a find rather than a request each.
  */
  const {hops: openCustodyHops} = useOpenCustodyHops()
  const [custodyRow, setCustodyRow] = useState<CertificateAdmin | null>(null)
  const [confirmArrivalRow, setConfirmArrivalRow] = useState<CertificateAdmin | null>(null)
  const openHopFor = useCallback(
    (id: string) => openCustodyHops.find(hop => hop.certificate_id === id),
    [openCustodyHops],
  )
  const custody = useCustodyMutations({
    onDispatched: () => {
      setCustodyRow(null)
      notification({type: 'success', title: 'Sent to location'})
      refetch()
    },
    onReceived: result => {
      setConfirmArrivalRow(null)
      notification({
        type: 'success',
        title: result.activated
          ? 'Arrival confirmed — certificate activated'
          : 'Arrival confirmed',
      })
      refetch()
    },
    onError: error => notification({type: 'error', title: getErrorMessage(error)}),
  })

  const getActionItems = useCallback(
    // VRG-231 fix G — `groupHasChildren` defaults to `true` (i.e. "don't hide
    // on this basis") so a non-parent row, or a caller that hasn't fetched the
    // group yet, never wrongly hides Split group; it only matters for a group
    // PARENT row, where `CertificateActionsCell` below resolves the real
    // children count before calling this. `groupIsSplittable` (VRG-231 review
    // round 2 item 3) defaults the same way — it only matters for an
    // INDIVISIBLE parent, where `CertificateActionsCell` resolves the real
    // membership (`canSplitCertificateGroup`) before calling this.
    (row: CertificateAdmin, groupHasChildren = true, groupIsSplittable = true): ActionDropdownItem[] => {
      // ---------------------------------------------------------------------------------
      // Standing rule (2026-08-12, VERA-455): an action is unavailable for exactly one of
      // three reasons, and each gets a different treatment.
      //   1. PERMISSION — the user lacks the grant. VISIBLE + `disabled` + `tooltip` naming
      //      the permission. The disabled row is the only channel telling them the
      //      capability exists and that an admin can grant it — resolution is owned by
      //      someone else, asynchronously, so the item has to stay discoverable.
      //   2. RECORD STATE — the row's own data makes it impossible right now (status,
      //      archived, ownership, an in-flight transfer). HIDE it. Resolution here is owned
      //      by the user and is one action away — the app owes them the RESOLVING action
      //      (e.g. Cancel transfer), not a tombstone explaining the blocked one. A per-row
      //      disabled item here is churn that never becomes a learned fact.
      //   3. IN-FLIGHT MUTATION — a request this menu triggered is still running
      //      (isActivating, isBulkActivating). Stays `disabled` + tooltip, NEVER hidden —
      //      hiding would make the item vanish under the cursor mid-click. This is
      //      transient UI state, not record state.
      // Permission never lives in `visible`; record state never lives in `disabled` (the
      // one deliberate exception is category 3, which is transient UI state, not record
      // state). See getBulkActions and CertificatePage.tsx for the same rule applied there.
      // ---------------------------------------------------------------------------------

      // VRG-268: available at ANY status. Restricting it to `inactive` excluded the case the
      // feature exists for — a tag damaged or lost on an item that has already been sold.
      // VRG-231 review round 2 item 12 — with ONE record-state exception: a group PARENT
      // is tagless by construction, so there is no tag to replace. Children keep it.
      const isVisibleReplaceTag = !isGroupParentRow(row)
      // VRG-388 — the row's tag state decides the LABEL, never the visibility. A tagless
      // certificate is not an error to hide the control from; it is the ADD case, and it is
      // how a link-only demo becomes a physical Tier-1 send. Both operations carry the same
      // permission, so this does not interact with the §27 disable+tooltip below.
      //
      // Raw columns, matching the server's guard: a `tag_uid` naming no `tags` row is still an
      // identifier, so treating it as absent would offer Add where the server refuses to add.
      const rowHasTag = Boolean(row.tag_uid ?? row.tag_id)
      // Category 2 (record state) — `inactive` is the ONLY status the endpoint accepts.
      // `transfer-certificate-to-owner.use-case.ts` 400s anything else: the four in-transfer
      // states with "already in a transfer state", everything else with 'should be in
      // "Inactive" status'. This read `=== 'active'` — the exact inverse — so every Transfer
      // the menu offered led to a 400 after submitting, and the one status that would have
      // worked never showed the action at all. That late failure is what §46 exists to
      // prevent, so the status lives in `visible`, never in `disabled`.
      //
      // Hiding creates an obligation (§46): the RESOLVING action must be reachable in the
      // state that hid this one. Mid-transfer -> `Cancel transfer` below returns the
      // certificate to `inactive`. `active` is not a blocker to resolve — it means an owner
      // has already claimed it, so it is not the brand's to hand out.
      // VRG-231 round 5 — predicate moved to utils/certificate: ONE definition per
      // action, shared by every kebab that offers it. Two of the four hand-written
      // copies had drifted; see that module's note.
      const isVisibleTransfer = isTransferAvailable(row.status)
      // Category 2 — Cancel transfer is the resolving action for the one transfer state it
      // covers. `isAdminCanCancel` reflects that only a store-initiated transfer can be
      // cancelled from here.
      const isVisibleCancelTransfer = isCancelTransferAvailable(row.status)
      // `'primary'` is the type `transfer-certificate-to-owner.use-case.ts` writes for a
      // brand-initiated transfer — the only kind a brand may call off. This compared against
      // `'store'`, which is not a member of the server's enum at all (see the TransferType
      // note in types/Certificate.ts), so it was constant false and this row never rendered.
      // That matters more now that Transfer correctly hides at `waiting_accept`: this IS the
      // resolving action §46 obliges us to leave reachable. An owner's own `sell`/`gift`
      // transfer stays excluded — it is not the brand's to cancel.
      const isAdminCanCancel = row?.transfer?.type === 'primary'
      // TODO(VERA-455 / TD-071): this predicate cannot mean what it is being asked to mean.
      // `DeactivateCertificateUseCase` sets status back to Inactive, so 'inactive' covers both
      // "never activated" and "was activated, billed, scanned, then deactivated" — and a hard
      // delete cascades away `scans` and `certificate_metering`. Needs an evidence-based check
      // (no metering row, no activation in the action log, no scans, no owner) enforced
      // SERVER-side, with archive as the default disposition. See VRG-274.
      //
      // VRG-273: visibility rule — `certificate_delete` is internal-scoped and no company
      // role can ever hold it (the API allowlist rejects granting it via a custom
      // role/override), so §27 says HIDE for non-internal users, not disable+tooltip (there
      // is no path to request access). An internal user who still lacks the grant keeps the
      // disabled+tooltip treatment below — their admin CAN grant it.
      // The shared delete predicate answers "is this record deletable"; the internal-
      // permission clause is a separate question (WHO may delete) and stays here —
      // it is a property of the viewer, not of the certificate.
      const isVisibleDelete =
        isDeleteAvailable(row) &&
        (isInternalUser || !isInternalPermission('certificate_delete'))
      const isVisibleActivate = isActivateAvailable(row.status)

      return [
        {
          label: 'Details',
          onClick: () => handleViewCertificate(row.id),
          icon: <InfoIcon strokeWidth={1} />
        },
        {
          label: 'View',
          onClick: () => handlePreviewCertificate({row}),
          visible: !!(row.tag_uid),
          icon: <ExternalLinkIcon strokeWidth={1}/>
        },
        {
          label: 'Activate',
          onClick: () => handleActivateCertificate(row),
          // §24: gates on `certificate_activate` — see the file-top note. Two independent
          // disable reasons (category 1 permission + category 3 in-flight mutation), so the
          // tooltip must name whichever is actually active — never leave it silently disabled.
          visible: isVisibleActivate,
          disabled: !canActivateCertificate || mutationState.isActivating,
          tooltip: !canActivateCertificate
            ? "You need the 'Activate certificate' permission. Ask an admin to grant it."
            : mutationState.isActivating
              ? 'Activating…'
              : undefined,
          icon: <BadgeCheckIcon strokeWidth={1} />,
        },
        {
          label: 'Send to location',
          onClick: () => setCustodyRow(row),
          // Category 2 (record state): hidden while a hop is already open (an item
          // cannot be in transit to two places — the API 409s) and once the item has
          // left the brand, because a customer's certificate is not the brand's stock.
          visible: !openHopFor(row.id) && isBrandHeldCertificate(row),
          disabled: !isCanTransfer,
          // Two tooltips — permission when disabled, what the action DOES when not.
          // Must match CertificatePage's row kebab word for word: the same action
          // explaining itself differently on two surfaces is the drift this pair is
          // checked against.
          tooltip: !isCanTransfer
            ? TRANSFER_PERMISSION_TOOLTIP
            : 'Record that this item is on its way to one of your locations. It stays yours in transit, and nothing is charged until arrival is confirmed there.',
          icon: <MapPinPlusIcon strokeWidth={1} />,
        },
        {
          label: 'Confirm arrival',
          onClick: () => setConfirmArrivalRow(row),
          visible: Boolean(openHopFor(row.id)),
          // Gated on `custody_receive`, NOT `transfer_certificate` — see the hook above.
          disabled: !isCanReceiveCustody || custody.isReceiving,
          tooltip: !isCanReceiveCustody
            ? CUSTODY_RECEIVE_PERMISSION_TOOLTIP
            : custody.isReceiving
              ? 'Confirming…'
              : 'Record that the item reached the location. If this certificate activates at location, confirming is what starts the charge.',
          icon: <MapPinCheckIcon strokeWidth={1} />,
        },
        {
          label: 'Transfer',
          onClick: () => handleTransferCertificate(row),
          // Category 2 (record state) — see the note above.
          visible: isVisibleTransfer,
          // Category 1 (permission) only from here down — state is fully excluded via
          // `visible` above.
          disabled: !isCanTransfer,
          tooltip: !isCanTransfer
            ? TRANSFER_PERMISSION_TOOLTIP
            : undefined,
          icon: <ArrowLeftRightIcon strokeWidth={1} />,
        },
        {
          // Category 2 (record state): only offered while a store-initiated transfer is
          // genuinely pending acceptance. Severs a pending transfer rather than destroying a
          // record, so it takes the "remove"-style X glyph (§38), not Trash.
          label: 'Cancel transfer',
          onClick: () => handleCancelTransferCertificate(row),
          visible: isVisibleCancelTransfer && isAdminCanCancel,
          // Category 1 (permission) — cancelling a transfer is gated by the same permission
          // as starting one.
          disabled: !isCanTransfer,
          tooltip: !isCanTransfer
            ? TRANSFER_PERMISSION_TOOLTIP
            : undefined,
          icon: <XIcon strokeWidth={1} />,
        },
        // Cancel transfer is offered at `waiting_accept` only, and that is CORRECT — the
        // other three transfer states belong to the customer-to-customer flow and are
        // cancelled by the two customers, not the brand. Full reasoning lives once next to
        // TRANSFER_STATES in utils/certificate.ts.
        {
          // Sentence case, matching the detail page and every other item in this menu.
          // VRG-388 — label follows the ROW's state (§46), kept in parity with the detail
          // page's header button per the kebab/detail action-parity rule.
          label: rowHasTag ? 'Replace tag' : 'Add tag',
          onClick: () => handleReplaceTagCertificate(row),
          // §27: disabled + tooltip naming the permission, not hidden.
          disabled: !canReplaceTag,
          tooltip: canReplaceTag
            ? undefined
            : "You need the 'Replace tag' permission. Ask an admin to grant it.",
          visible: isVisibleReplaceTag,
          icon: rowHasTag ? <ReplaceIcon strokeWidth={1} /> : <TagIcon strokeWidth={1} />
        },
        {
          // VRG-231 review item 7 — same gating as the certificate detail header's
          // overflow entry: hidden entirely once already in a group (a record
          // property, not a permission), disable+tooltip when the permission is
          // missing (§27, in-context action).
          // VRG-231 review round 3 — the OWNERSHIP case now disables with a reason
          // instead of opening a modal that then refuses. Opening a modal to say
          // "you can't do this" is the worst of both: it costs a click and a
          // context switch to deliver a sentence the menu could have carried.
          //
          // The two ineligibility reasons are treated DIFFERENTLY, and the split is
          // §46's own logic taken one step further. §46 says record state HIDES,
          // because the resolving action is normally one click away. That premise
          // holds for one of these reasons and not the other:
          //
          //   "Already part of a certificate group" — RESOLVABLE. Remove it from
          //     that group and it becomes eligible. §46 applies as written: hide,
          //     and let "Remove from group" be the action on offer instead. This is
          //     the existing `visible` predicate, unchanged.
          //   "Has an owner (or ownership history)" — PERMANENT. Ownership history
          //     is never cleared, so no action the user can take makes this
          //     certificate groupable, ever. Hiding a permanently-impossible action
          //     teaches nothing and reads as a missing feature to someone who came
          //     looking for it, which is what happened here. Disable + say why, once.
          //
          // So: record state hides when the state is RESOLVABLE, and disables with a
          // reason when it is permanent. Both still obey §46's absolute forcing
          // function — a disabled item carries a tooltip.
          //
          // The reason string comes from `getIneligibleReason`, the same helper the
          // modal and the bulk-bar use, which itself mirrors the server's
          // preconditions. Never retype it here (§44's drift argument applies to UI
          // strings as much as to definitions).
          label: 'Create group',
          onClick: () => handleOpenCreateGroupModal(row),
          visible: !row.parent_certificate_id && !row.group_divisibility,
          disabled: !isCanManageGroups || Boolean(getIneligibleReason(row)),
          tooltip: !isCanManageGroups
            ? "You need the 'Manage Certificate Groups' permission. Ask an admin to grant it."
            : getIneligibleReason(row) ?? undefined,
          icon: <MergeIcon strokeWidth={1} />,
        },
        {
          // VRG-231 review round 2 item 11 — "Split group" on the list row kebab,
          // matching the detail page's header action. NOT destructive (review round
          // 2 item 7) — a split deletes no record, it only severs a relationship
          // (§32/§38).
          // VRG-231 review round 2 item 3 — SUPERSEDES the old "divisible only"
          // gate. An indivisible group's parent can ALSO reach Split group while
          // every member (parent + children) is still not-yet-live and unowned —
          // see `canSplitCertificateGroup` (utils/certificate.ts), which mirrors
          // the server's `assertIndivisibleGroupIsSplittable`. `groupIsSplittable`
          // is resolved by `CertificateActionsCell` below, not here — this callback
          // can't call hooks itself.
          // VRG-231 fix G — ALSO hidden (record state, §46) once the group has no
          // remaining children (a parent left behind by an older split): the modal
          // would otherwise open on an empty list with a permanently-disabled Split
          // button, a dead end. `groupHasChildren` is resolved the same way.
          // VRG-231 review round 3 — `isGroupParentRow(row)` ADDED, and it is the
          // whole fix for "Split group shows on a certificate that is in no group".
          //
          // The condition was `!row.parent_certificate_id && groupIsSplittable &&
          // groupHasChildren`, and every one of those is TRUE for an ordinary
          // standalone certificate: it has no parent, and `CertificateActionsCell`
          // defaults BOTH resolved values to `true` for a non-parent row (its
          // `groupHasChildren = !isGroupParent || …` and `groupIsSplittable =
          // isGroupParent ? … : true`). So Split group rendered on EVERY top-level
          // row in the list. Reported from the UI; reproduced on a certificate with
          // `group_divisibility: null` and `parent_certificate_id: null`.
          //
          // What makes this worth the long comment: the two defaults were justified
          // by a comment in `CertificateActionsCell` asserting "the item's own
          // `visible` already requires `group_divisibility === 'divisible' &&
          // !parent_certificate_id`" — a requirement that was NOT in this expression.
          // The defaults were inert only under a guard nobody had written, so the
          // comment read as coverage while the code had none. When a resolved value
          // is documented as "inert for this case", check the call site actually
          // makes it inert.
          label: 'Split group',
          onClick: () => handleOpenSplitGroupModal(row),
          visible:
            isGroupParentRow(row) &&
            groupIsSplittable &&
            isCanManageGroups &&
            groupHasChildren,
          icon: <SplitIcon strokeWidth={1} />,
        },
        {
          /*
            VRG-836 — "Convert to real": clears this row's `demo` badge AND bills the
            activation (`POST /demo-data/certificates/:id/convert`).

            §73 — placed immediately BEFORE Archive, which stays the last non-destructive
            item. Convert is not destructive (§32): it ends a classification, creates
            nothing and destroys nothing, so no `destructive` treatment and no red.

            §46 — RECORD STATE gates `visible`, three ways, and each one is a state in
            which the server would refuse or the offer would be noise:
              - only a `demo` row can convert (`intro` samples and real rows are outside
                the endpoint's own WHERE clause);
              - not inside a demo COMPANY, where the server refuses with
                `company-is-still-a-demo` (a cleared row would never meter there) and where
                every row is Vera's anyway, so `ExampleKindBadge` is suppressed for the same
                reason — the affordance appears exactly where the badge does;
              - the row needs a tag, because metering has nothing to write without one.
            IN-FLIGHT stays `disabled` + tooltip, scoped to this row only, never hidden.

            No PERMISSION gate here: `sample_data_manage` is held by `company_admin` and by
            the two internal roles, which is every role that can reach this page's actions,
            so there is no grant a viewer could be missing. That the permission's DESCRIPTION
            says nothing about being billed is exactly why the confirm names the cost — see
            `ConvertDemoRowModal`.
          */
          label: 'Convert to real',
          onClick: () => handleConvertDemoCertificate(row),
          // `rowHasTag` is reused deliberately rather than re-deriving the tag read: it is the
          // SAME derivation the Add/Replace tag control above uses, so the two cannot disagree
          // about whether this row has a tag. Hiding here leaves "Add tag" reachable, which is
          // the obligation §46 attaches to a record-state hide — the user is offered the action
          // that RESOLVES the state, not a tombstone explaining the blocked one.
          visible: row.example_kind === 'demo' && !isDemoCompany && rowHasTag,
          disabled: isConvertingDemoCertificate && convertingCertificateId === row.id,
          tooltip:
            isConvertingDemoCertificate && convertingCertificateId === row.id
              ? 'Converting…'
              : undefined,
          icon: <FlaskConicalOffIcon strokeWidth={1} />,
        },
        {
          // Archive is reversible (Unarchive exists) — not destructive. It still gets
          // a divider above it, grouping it with Delete as the "record management"
          // actions, separate from the everyday View/Activate/Transfer/Replace tag ones.
          label: 'Archive',
          onClick: () => handleArchiveCertificate(row),
          // `archived_at` — `row.archived` is never emitted, so this predicate was
          // constantly true and the row offered Archive on already-archived rows.
          visible: !row.archived_at,
          divider: true,
          icon: <ArchiveIcon strokeWidth={1} />,
        },
        {
          // VRG-231 review — a group PARENT's delete goes through the
          // dedicated `DELETE /certificates/admin/groups/:id` route (see
          // DeleteCertificateModal), which requires manage_certificate_groups
          // — a DIFFERENT permission than the plain single-certificate delete
          // this trigger otherwise gates on (transfer_certificate). Without
          // this, a user with transfer_certificate but not
          // manage_certificate_groups could open the modal and hit a 403.
          // Disable+tooltip (§27, in-context action), scoped to group
          // parents only — a non-group certificate's delete gating is
          // unchanged.
          label: 'Delete',
          onClick: () => handleDeleteCertificate(row),
          // §24: gates on `certificate_delete`. A group PARENT row additionally routes through
          // the group-delete endpoint, which requires `manage_certificate_groups`; both are
          // PERMISSION blocks, so both disable+tooltip rather than hide (§46).
          disabled: !canDeleteCertificate || (isGroupParentRow(row) && !isCanManageGroups),
          tooltip: !canDeleteCertificate
            ? "You need the 'Delete certificates' permission. Ask an admin to grant it."
            : isGroupParentRow(row) && !isCanManageGroups
            ? "You need the 'Manage Certificate Groups' permission. Ask an admin to grant it."
            : undefined,
          visible: isVisibleDelete,
          destructive: true,
          icon: <TrashIcon strokeWidth={1} color={theme.palette.error.main} />,
        },
        {
          // VRG-512 (AC4) — "Remove sample": deletes a single seeded `intro` certificate
          // (and its fabricated scans) via `DELETE /intro-data/certificates/:id`.
          //
          // Own divider — this is not part of the "record management" group above it
          // (Archive/Delete apply to every certificate; this applies to none of a normal
          // tenant's rows), so it reads as its own, separate capability rather than a
          // third member of that set.
          //
          // §38 (X vs Trash): a genuine record destruction — the row disappears from the
          // table and its scans are deleted with it (the server response's own
          // `scansDeleted` count) — so Trash, not X, same as the real Delete above.
          //
          // §46: no PERMISSION gate — `INTRO_ROLES` on the server (platform_admin,
          // platform_manager, company_admin, company_team_member) covers every role that
          // can reach this page at all, so there is no grant a viewer of this table could
          // be missing. RECORD STATE gates `visible` (only a seeded `intro` row can be
          // removed this way — `demo` rows and real certificates are untouched by the
          // endpoint's own WHERE clause, so there is nothing to offer on them). IN-FLIGHT
          // stays `disabled` + tooltip, scoped to just the row actually being deleted via
          // `deletingCertificateId`, never hidden.
          label: 'Remove sample',
          onClick: () => handleRemoveSampleCertificate(row),
          visible: row.example_kind === 'intro',
          disabled: isDeletingIntroCertificate && deletingCertificateId === row.id,
          tooltip:
            isDeletingIntroCertificate && deletingCertificateId === row.id
              ? 'Removing…'
              : undefined,
          divider: true,
          destructive: true,
          icon: <TrashIcon strokeWidth={1} color={theme.palette.error.main} />,
        },
      ]
    },
    [isCanTransfer, isCanManageGroups, canReplaceTag, canActivateCertificate, canDeleteCertificate, isInternalUser, mutationState.isActivating, primaryInstallation, notification, theme, isPrimaryLoading, isPrimaryError, refetchPrimaryInstallation, handleArchiveCertificate, isDeletingIntroCertificate, deletingCertificateId, isDemoCompany, isConvertingDemoCertificate, convertingCertificateId]
  )

  // VRG-231 review round 2 item 11 — row kebab for a CHILD row (a real Tree
  // Data row now, not a detail-panel entry — see `CertificateActionsCell`
  // above). Mirrors CertificatePage.tsx's own getGroupMemberActionItems
  // (Preview / Details / Replace tag / Transfer / Remove from group / Delete —
  // full certificate action set, minus Activate/Archive/Create group/Split
  // group, which stay top-level-row-only actions).
  const getGroupMemberActionItems = useCallback(
    (row: CertificateAdmin): ActionDropdownItem[] => {
      // ⚠️ VRG-231 round 5 — Transfer was `=== 'active'` here: INVERTED, and the
      // inversion made it unreachable under the product's DEFAULT activation
      // trigger (ON_PRIMARY_TRANSFER — "certificates activate when the customer
      // claims ownership"), where the transfer is WHAT ACTIVATES. Requiring active
      // first is circular. All predicates now come from utils/certificate, so this
      // kebab and the same certificate's row on the list cannot answer differently.
      const isVisibleReplaceTag = isReplaceTagAvailable(row.status, isGroupParentRow(row))
      const isVisibleTransfer = isTransferAvailable(row.status)
      const isInTransferState = isCertificateInTransferState(row.status)
      const isVisibleDelete = isDeleteAvailable(row)
      // VRG-231 review round 2 item 3 — SUPERSEDES the old "divisible only" gate
      // on this child's own "Remove from group". An indivisible group's child can
      // ALSO leave the group while every member (parent + every child) is still
      // not-yet-live and unowned — see `canSplitCertificateGroup`
      // (utils/certificate.ts). The parent's own row (status/owner) comes from
      // `certificatesById` — every parent with a rendered child is, by
      // construction, also a top-level row already on screen — and its siblings
      // from `childrenByParentId`, both already fetched at the page level (no
      // extra request here).
      const groupParentRow = row.parent_certificate_id
        ? certificatesById.get(row.parent_certificate_id)
        : undefined
      const groupSiblings = row.parent_certificate_id
        ? childrenByParentId.get(row.parent_certificate_id) ?? []
        : []
      const groupIsSplittable = canSplitCertificateGroup(row.group_divisibility, {
        parent: groupParentRow,
        children: groupSiblings,
      })

      return [
        {
          label: 'Preview',
          onClick: () => handlePreviewCertificate({ row }),
          visible: !!(row.tag_uid),
          icon: <EyeIcon strokeWidth={1} />,
        },
        {
          label: 'Details',
          onClick: () => handleViewCertificate(row.id),
          visible: true,
          icon: <InfoIcon strokeWidth={1} />,
        },
        {
          label: 'Replace Tag',
          onClick: () => handleReplaceTagCertificate(row),
          visible: isVisibleReplaceTag,
          icon: <ReplaceIcon strokeWidth={1} />,
        },
        {
          label: 'Transfer',
          onClick: () => handleTransferCertificate(row),
          visible: (isVisibleTransfer || isInTransferState) && isCanTransfer,
          disabled: isInTransferState,
          icon: <ArrowLeftRightIcon strokeWidth={1} />,
        },
        {
          // VRG-231 fix D — "Remove from group" (was "Split from group"):
          // renamed because on a CHILD this removes only THIS member, not the
          // whole group ("Split group" — the PARENT's own action, above in
          // `getActionItems` — keeps that name since it dissolves everything).
          // Severs this child's link only; the certificate itself survives, so
          // Unlink (not Trash) per §38.
          //
          // VRG-231 review round 2 item 3 — SUPERSEDES the old "divisible only"
          // gate: see `groupIsSplittable` above.
          //
          // VRG-231 fix E — opens the confirm modal (disclosing a full-group
          // dissolve when this removal would trigger one) rather than firing
          // the unlink immediately on click.
          label: 'Remove from group',
          onClick: () => handleOpenRemoveFromGroupModal(row),
          visible: Boolean(row.parent_certificate_id) && groupIsSplittable && isCanManageGroups,
          icon: <UnlinkIcon strokeWidth={1} />,
        },
        {
          // Same gating as the top-level row's own Delete above — a group
          // PARENT row (rendered here inside an expanded group's detail
          // panel) deletes via the group-delete route, which requires
          // manage_certificate_groups.
          label: 'Delete',
          onClick: () => handleDeleteCertificate(row),
          // §24: gates on `certificate_delete`. A group PARENT row additionally routes through
          // the group-delete endpoint, which requires `manage_certificate_groups`; both are
          // PERMISSION blocks, so both disable+tooltip rather than hide (§46).
          disabled: !canDeleteCertificate || (isGroupParentRow(row) && !isCanManageGroups),
          tooltip: !canDeleteCertificate
            ? "You need the 'Delete certificates' permission. Ask an admin to grant it."
            : isGroupParentRow(row) && !isCanManageGroups
            ? "You need the 'Manage Certificate Groups' permission. Ask an admin to grant it."
            : undefined,
          visible: isVisibleDelete,
          destructive: true,
          divider: true,
          icon: <TrashIcon strokeWidth={1} color={theme.palette.error.main} />,
        },
      ]
    },
    [isCanTransfer, isCanManageGroups, canDeleteCertificate, theme, certificatesById, childrenByParentId]
  )

  /**
   * The selected certificates that this bulk action will ACTUALLY activate.
   *
   * Already-live certificates are excluded: `BulkActivateCertificatesUseCase`
   * skips them, so including them in the count would name a number the action
   * does not perform (AC-12).
   */
  const bulkActivatableCertificates = useMemo(
    () =>
      certificates.filter(
        c => selectedIds.includes(c.id) && isActivateAvailable(c.status),
      ),
    [certificates, selectedIds],
  )


  /**
   * Label + hover text for a bulk action that only applies to some of the selection.
   *
   * Returns a plain label when everything qualifies: "(3/3)" is noise, and noise is
   * how people learn to ignore the one that mattered.
   */
  const bulkEligibility = (
    eligible: number,
    selected: number,
    explain: (excluded: number) => string,
  ): {suffix: string; hint?: string} => {
    if (eligible >= selected) return {suffix: ''}
    return {
      suffix: ` (${eligible}/${selected})`,
      hint: explain(selected - eligible),
    }
  }

  const getBulkActions = useCallback((selectedRows: CertificateAdmin[]) => {
    const deletableRows = selectedRows.filter(isDeleteAvailable)
    const activatableRows = selectedRows.filter(row => isActivateAvailable(row.status))
    // `inactive` is the ONLY status the transfer endpoint accepts, and it is not one of
    // the four transfer states, so the old `&& !isCertificateInTransferState(row.status)`
    // half was always true. Dropped rather than kept as reassurance — a condition that
    // cannot be false reads as if it were guarding something.
    const transferableRows = selectedRows.filter(row => isTransferAvailable(row.status))

    const activateEligibility = bulkEligibility(
      activatableRows.length,
      selectedRows.length,
      n =>
        `${n} of the selected ${n === 1 ? 'certificate is' : 'certificates are'} already active or in a state that cannot be activated, so ${n === 1 ? 'it is' : 'they are'} left out. Nothing is charged for them.`,
    )
    const transferEligibility = bulkEligibility(
      transferableRows.length,
      selectedRows.length,
      n =>
        `${n} of the selected ${n === 1 ? 'certificate cannot' : 'certificates cannot'} be transferred right now — already transferred, or a transfer is in flight.`,
    )
    /*
      THE ELIGIBILITY FRACTION IS BACK, WITH THE EXPLANATION IT WAS MISSING.

      VRG-231 fix A removed it, and that removal was right about the thing it
      diagnosed: a bare "Activate (1/2)" is a fraction with no explanation, and
      "why only 1?" with no answer reads as a bug. The conclusion drawn was that
      the count belongs only at the commit point.

      But that leaves the bulk bar silent about a mismatch the user can see with
      their own eyes — they selected five rows and the button acts on one — and
      makes them commit to find out. Spencer asked for it back (2026-09-03), and
      the reconciliation is not to choose between the two positions: show the
      fraction AND answer the question, on hover, where an explanation fits.

      So the fraction appears only when it is NOT 1/1 (no fraction is information
      when everything qualifies), and it always carries a tooltip naming why the
      rest are excluded. The commit-point disclosure stays exactly as it was — it
      is what the user acts on; this is what stops them being surprised by it.
    */
    return [
      {
        label: `Activate${activateEligibility.suffix}`,
        onClick: () => handleBulkActivate(selectedRows),
        icon: <BadgeCheckIcon strokeWidth={1} />,
        // §24 + the three-category rule above (getActionItems): `visible` already requires
        // at least one activatable row, so the old `activatableRows.length === 0` half of
        // `disabled` was unreachable and just left a silent-disabled path — removed.
        // `disabled` is category 1 (permission) + category 3 (in-flight bulk mutation) only.
        visible: activatableRows.length > 0,
        disabled: isBulkActivating || !canActivateCertificate,
        // A disable REASON always wins over the eligibility hint: a disabled control
        // must say why it is disabled (§46), and "3 of 5 qualify" does not answer that.
        tooltip: !canActivateCertificate
          ? "You need the 'Activate certificate' permission. Ask an admin to grant it."
          : isBulkActivating
            ? 'Activating…'
            : activateEligibility.hint,
      },
      {
        label: `Transfer${transferEligibility.suffix}`,
        // Opens the shared transfer modal in batch mode. The warning about the
        // non-qualifying rows is NOT fired here as a toast any more — the modal states
        // the split ("3 of 5", with the reason per excluded row) where the user is
        // about to commit, rather than in a toast that disappears before the decision.
        onClick: () => handleBulkTransfer(selectedRows),
        icon: <ArrowLeftRightIcon strokeWidth={1} />,
        // §46: record state in `visible` (nothing selected qualifies -> the action is
        // not offered); permission in `disabled` + `tooltip`.
        //
        // There is no in-flight case to disable for here, unlike bulk Activate: this
        // action only OPENS the modal, and the mutation lives inside it, behind its own
        // disabled submit. The menu is closed while the modal is up, so it cannot be
        // re-fired mid-request.
        visible: transferableRows.length > 0,
        disabled: !isCanTransfer,
        tooltip: !isCanTransfer
          ? TRANSFER_PERMISSION_TOOLTIP
          : transferEligibility.hint,
      },
      {
        // VRG-231 item C — "Group certificates" bulk entry point, opening
        // CreateCertificateGroupModal pre-seeded with the selection (VRG-337's
        // "group existing certificates" flow). Visible once >= 2 rows are
        // selected, REGARDLESS of eligibility — the click handler itself
        // filters ineligible rows and reports how many were dropped (see
        // handleOpenCreateGroupModalBulk), the same pattern as Activate/Delete
        // above rather than hiding/disabling per-row at the bulk-bar level.
        label: 'Group certificates',
        onClick: () => handleOpenCreateGroupModalBulk(selectedRows),
        icon: <MergeIcon strokeWidth={1} />,
        visible: selectedRows.length >= 2,
        disabled: !isCanManageGroups,
        tooltip: !isCanManageGroups
          ? "You need the 'Manage Certificate Groups' permission. Ask an admin to grant it."
          : undefined,
      },
      {
        label: 'Delete',
        onClick: () => handleBulkDelete(selectedRows),
        icon: <TrashIcon strokeWidth={1} />,
        destructive: true,
        // §24: bulk Delete gates on `certificate_delete`, same as the row-level action above.
        // VRG-273: HIDE for non-internal users — see the row-level `isVisibleDelete` note
        // above for why (no company role can ever hold this permission). That predicate is
        // permission-in-`visible` deliberately (no path to access exists at all) and is left
        // untouched; only the redundant `disabled` half below is removed, same as Activate.
        visible: deletableRows.length > 0 && (isInternalUser || !isInternalPermission('certificate_delete')),
        disabled: !canDeleteCertificate,
        tooltip: canDeleteCertificate
          ? undefined
          : "You need the 'Delete certificate' permission. Ask an admin to grant it.",
      },
    ]
  }, [isBulkActivating, isCanTransfer, isCanManageGroups, canActivateCertificate, canDeleteCertificate, isInternalUser, notification, handleBulkTransfer])

  const columns = useMemo(
    () => [
      {
        field: 'certificate',
        headerName: 'Certificate',
        type: 'string',
        // VRG-231 Tree Data migration — no longer `pinned: 'left'`/rendered as
        // its own grid column. DataGridPro's auto-inserted TREE column
        // (`groupingColDef`, in the Table JSX below) now occupies that exact
        // leftmost slot and renders the same `CertificateCell` content plus
        // the tree toggle/indentation — that's what fixed the odd
        // indent/padding this column used to have from the OLD row Detail
        // Panel's own toggle column. This colDef stays in `columns` (hidden
        // via `visibilityModel={{ certificate: false, ... }}` below) purely
        // because `columns` also drives the Filters drawer's search target
        // (`searchParams` below — this IS the "Certificate" scan target) and
        // CSV export's column list; neither of those cares whether the grid
        // itself renders the column.
        //
        // vs Product, which is the pair readers most often collapse into one thing: a
        // product is the design, a certificate is one physical unit of it.
        renderHeader: headerWithTerm('Certificate', 'certificate'),
        // 434px, up from 368. The number is derived, not chosen: the grid's content
        // starts 282px from the viewport left (280px sidebar + the 1px grid border +
        // the 1px collapsed bulk-select filler), and the toolbar unit this column is
        // meant to line up with ends at 716px — so 716 - 282 = 434.
        //
        // 368 was the SAME derivation against a toolbar that ended at 650: the Search
        // field alone (x 350, minWidth 300, right 650). It went stale when
        // `ScanTargetButton` joined the row to the RIGHT of Search (ActionLine, an
        // 8px-gap Stack), extending the unit by the button's 58px + that 8px gap = 66,
        // which is exactly 434 - 368. Both numbers are measured, not computed from the
        // Button's padding tokens.
        //
        // Caveat worth knowing before re-tuning this: since VRG-325 the shared Table
        // converts every authored `width` into a `flex` share with `minWidth` = the
        // authored width, so 434 is the width this column renders at when the grid is
        // NARROW enough that columns freeze at their minWidths. On a wide viewport
        // every column — this one included — is scaled up proportionally, so the right
        // border sits past the scan button there. That scaling is table-wide and
        // deliberate; the authored number is still the design datum.
        width: 434,
        align: 'left',
        // TODO: make searchable as well in Table search not just previews...
        // TODO: make product.title work vs needing the reference param
        searchParams: { fields: ['title', 'id'], preview: 'certificates' },
        // `certificate` is a synthetic field — the row has no such key, so CSV export
        // has nothing to fall back to and would emit blank. Mirror what the cell shows.
        csvValue: (row: any) => row.product?.title || row.id || '',
        // VRG-265 — the value the FILTER compares. The drawer's Certificate field (and its scan
        // target) carries certificate ids, and the grid's `isAnyOf` compares `row[field]`; with
        // no key of that name it compared `undefined` and matched nothing, so an applied filter
        // emptied the table. Same reason on `owner` and `product` below.
        valueGetter: (_value: any, row: any) => row?.id,
        renderCell: ({ row, value }) => (<CertificateCell certificate={row} width={260} showGroupBadge />),
      },
      // {
      //   field: 'product',
      //   headerName: 'Product',
      //   type: 'string',
      //   renderCell: (params: GridRenderCellParams) => (
      //     <Link component={RouterLink} to={ROUTES.PRODUCT(params.row.product.id)}>
      //       <Typography variant="body3" noWrap sx={{ display: 'block' }}>
      //         {params.row.product.title}
      //       </Typography>
      //     </Link>
      //   ),
      // },

      // {
      //   field: 'availabile',
      //   headerName: 'Availability',
      //   type: 'string',
      //   width: 150,
      //   renderCell: ({ value }) => (
      //   )
      // },
      // {
      //   field: 'product_id',
      //   dataIndex: ['product', 'id'],
      //   headerName: 'Product ID',
      //   renderCell: ({ row, value }) => (
      //     <Link component={RouterLink} to={ROUTES.PRODUCT(value)}>
      //       <Typography variant="body3" sx={{ display: 'block' }}>
      //         {value}
      //       </Typography>
      //     </Link>
      //   ),
      // },
      // {
      //   field: 'e_tag_id',
      //   headerName: 'Tag ID',
      //   type: 'string',
      //   renderCell: (params: GridRenderCellParams) => (
      //     <Link component={RouterLink} to={ROUTES.TAGS(params.row.e_tag_id)}>
      //       <Typography variant="body3" noWrap sx={{ display: 'block' }}>
      //         {params.row.product.title}
      //       </Typography>
      //     </Link>
      //   ),
      // },
      {
        field: 'created_at',
        headerName: 'Created',
        type: 'dateTime',
        valueGetter: (value) => value && new Date(value),
        // VERA-304 verify-walkthrough feedback: one date format everywhere —
        // ordinal day, 12-hour time (working-conventions).
        valueFormatter: (value: any) =>
          // EMPTY_VALUE, not a bare '-': these two date columns sat immediately beside
          // the Owner column, which draws the em dash, so one table showed two
          // different characters for the same statement. One constant, one glyph.
          value ? datetime(value, 'MMM do yyyy, h:mm a') : EMPTY_VALUE,
        width: 220
      },
      {
        field: 'status',
        headerName: 'Status',
        // statusChip = the scans-tab style filter (icon + color toggles). Options carry
        // the full status set (icon + color) so the drawer lists ALL statuses, not just
        // those on the current server-paginated page. Table cell rendering is unchanged.
        type: 'statusChip',
        options: CERT_STATUS_FILTER_OPTIONS,
        renderCell: ({ value }) => (<StatusChipCell status={value} statuses={CERTIFICATE_STATUSES} />),
        width: 150
      },
      {
        field: 'scan_counter',
        headerName: 'Scans',
        type: 'number',
        // 140: 120 left no room for the term glyph (14px + 4px gap) beside the label
        // and the sort arrow (§49 — a header is never truncated).
        width: 140,
        renderHeader: headerWithTerm('Scans', 'scan'),
      },
      // {
      //   field: 'product_type',
      //   dataIndex: ['product', 'type'],
      //   headerName: 'Product Type',
      //   width: 120
      // },
      {
        field: 'owner',
        headerName: 'Owner',
        type: 'string',
        // 280 already clears label + chrome + glyph by a wide margin; unchanged.
        width: 280,
        align: 'left',
        renderHeader: headerWithTerm('Owner', 'owner'),
        // `reference: 'customer'` is what the dropdown SHOWS — the joined users row (name,
        // email, avatar). `valueField: 'owner_id'` is what the filter SENDS, and the column has
        // to say so because they are different entities: `customer.id` is a USERS id (the query
        // joins certificates → owners → users and returns the user as `customer`), while the
        // list's `ownerId` param filters `certificates.owner_id`, an OWNERS id. Picking an owner
        // emitted `customer.id` and came back with zero rows — a real id, of the wrong entity.
        searchParams: {fields: ['first_name', 'last_name'], preview: 'owners', reference: 'customer', valueField: 'owner_id'},
        // Synthetic field (see the certificate column) — needs an explicit CSV value.
        // Mirrors what the cell renders, including the two absent-owner states: an export
        // that read blank where the screen reads "Louis Vuitton — Not yet sold" would
        // reintroduce, in the file, exactly the "missing data" impression this change removes.
        csvValue: (row: any) => {
          const c = row.customer
          if (!c) {
            const brand = row.company ?? currentCompany
            if (isBrandHeldCertificate(row) && brand?.name) {
              return `${brand.name} (${BRAND_HELD_QUALIFIER.toLowerCase()})`
            }
            return EMPTY_VALUE
          }
          const name = [c.first_name, c.last_name].filter(Boolean).join(' ').trim()
          return name || c.email || ''
        },
        // NOT the filter's value — that is `searchParams.valueField` above. This is the CELL
        // value the DataGrid itself works on, and it matters here for one reason: the drawer
        // commits to BOTH layers (`Filters.commitFilters` calls `onFiltersChange` AND
        // `setFilterModel`), and this list runs the grid in its default client-side
        // `filterMode`. So the grid re-applies `owner isAnyOf [<owners id>]` to the rows the
        // server already narrowed, comparing against THIS value. Left as `customer.id` it
        // matched nothing and emptied the table a second time, after the server had returned
        // the right rows. It must be the same id the filter carries.
        valueGetter: (_value: any, row: any) => row?.owner_id,
        // One shared cell decides all three owner states (owner / brand-held / unclaimed)
        // so this column and the certificate detail cannot drift apart — §36.
        renderCell: ({ row }: GridRenderCellParams) => (
          <CertificateOwnerCell certificate={row} company={currentCompany} />
        ),
      },
      {
        field: 'date_of_ownership',
        headerName: 'Last ownership date',
        type: 'dateTime',
        valueGetter: (value) => value && new Date(value),
        // VERA-304 verify-walkthrough feedback: one date format everywhere —
        // ordinal day, 12-hour time (working-conventions).
        valueFormatter: (value: any) =>
          // EMPTY_VALUE, not a bare '-': these two date columns sat immediately beside
          // the Owner column, which draws the em dash, so one table showed two
          // different characters for the same statement. One constant, one glyph.
          value ? datetime(value, 'MMM do yyyy, h:mm a') : EMPTY_VALUE,
        width: 220
      },
      {
        field: 'id',
        headerName: 'ID',
        type: 'string',
        width: 200,
        renderCell: ({ row, value }) => (<LinkCell link={value} route={ROUTES.CERTIFICATE(value)} />),
      },
      {
        field: 'product',
        headerName: 'Product',
        type: 'string',
        searchParams: {fields: ['title', 'sku'], preview: 'products', reference: 'product'},
        width: 280,
        align: 'left',
        // The Product filter (and its scan target) carries product ids — see the certificate
        // column. CSV keeps showing the title, which is what the cell shows.
        valueGetter: (_value: any, row: any) => row?.product?.id,
        csvValue: (row: any) => row.product?.title || '',
        renderCell: ({ row, value }) => (<ProductCell product={row.product}/>),
      },
      {
        field: 'actions',
        headerName: false,
        sortable: false,
        align: 'center',
        width: 56,
        pinned: 'right',
        // VRG-231 Tree Data migration — `CertificateActionsCell` now picks between
        // `getActionItems` (top-level rows) and `getGroupMemberActionItems` (a
        // group's child rows, which render as real rows here too — see the `Table`
        // JSX below) based on whether the row carries a `parent_certificate_id`.
        renderCell: ({ row }) => (
          <CertificateActionsCell
            row={row}
            getActionItems={getActionItems}
            getGroupMemberActionItems={getGroupMemberActionItems}
          />
        ),
      },
    ], [isCanTransfer, currentCompany, getGroupMemberActionItems]
  )

  // ],
  // [isCanTransfer]
  // ) as ColumnsType<CertificateAdmin>[]

  // VRG-231 Tree Data — the column DataGridPro auto-inserts to render the tree
  // (indentation + expand/collapse toggle), taking the exact leftmost pinned
  // slot the old `certificate` column occupied (see that colDef's own note
  // above). This relies on MUI's DEFAULT `renderCell` for the column
  // (`GridTreeDataGroupingCell`, which already handles indentation via
  // `rowNode.depth` and the expand/collapse toggle via
  // `rowNode.childrenExpanded` — see its source in @mui/x-data-grid-pro) by
  // supplying only `valueFormatter` below, which the default renderCell
  // displays verbatim as `formattedValue`. Returning a REACT NODE from a
  // `valueFormatter` is unusual but valid — DataGridPro's cell pipeline never
  // assumes the formatted value is a string — and nothing else reads this
  // synthetic column (CSV export and the Filters drawer both read the real
  // `certificate` colDef above instead, which is why that one is kept, just
  // hidden).
  const groupingColDef = useMemo(
    () => ({
      headerName: 'Certificate',
      // VRG-231 review round 3 — the term tooltip PLUS the expand/collapse-all
      // control, which lives here rather than in the toolbar because it acts on
      // this column alone and only exists when the page has a group at all.
      // `ExpandAllGroupsToggle` documents that choice in full; it renders null
      // when there are no groups, so an ungrouped page's header is unchanged.
      // `width: '100%'` here AND `fillHeaderContent` below are BOTH load-bearing:
      // the toggle right-aligns with `ml: 'auto'`, which is inert unless every box
      // between it and the header cell fills. MUI's own content box shrinks to fit
      // (see the `fillHeaderContent` rule in Table.tsx), so setting only one of the
      // two leaves the control flush against the title — which is how it first
      // shipped and what the round-3 report was looking at.
      headerClassName: 'fillHeaderContent',
      renderHeader: () => (
        <Stack direction="row" alignItems="center" sx={{ minWidth: 0, width: '100%' }}>
          {CertificateTermHeader()}
          <ExpandAllGroupsToggle />
        </Stack>
      ),
      width: 434,
      // The member-count badge already shows inline (CertificateCell's own
      // `showGroupBadge`, on a PARENT row) — MUI's own descendant count would
      // just repeat that number in a second place right next to it.
      hideDescendantCount: true,
      valueFormatter: (_value: any, row: CertificateAdmin) => {
        const isChild = Boolean(row.parent_certificate_id)
        return (
          // VRG-231 review round 2 item 1 — was `spacing={1}` (4px), which read as
          // the glyph crammed against the thumbnail. `spacing={2}` (8px) gives it
          // room without the row starting to look like two unrelated groups.
          <Stack direction="row" spacing={2} alignItems="center" sx={{ minWidth: 0 }}>
            {isChild && (
              // VRG-231 item 3 — the requested down-and-right glyph marking a
              // CHILD row as visually subordinate to its parent (the
              // convention used elsewhere in Vergence). The PARENT keeps its
              // existing role glyph (CertificateCell's own `showGroupBadge`,
              // unchanged) — this is additive, for children only.
              //
              // VRG-231 review round 2 item 1 — was `theme.palette.text.secondary`
              // (a muted grey tint), which read as dim against the row. `text.primary`
              // is the theme's own "full strength, not a tint" ink — full black in
              // light mode, full white (`#fafafa`, darkPalette override) in dark mode.
              // VRG-231 review round 3 — the glyph now sits the SAME distance from
              // the thumbnail as a PARENT row's expand chevron does from its own:
              // 16px, measured (parent chevron glyph right 145 -> image left 161).
              // The child was at 9px, which read as crammed against the image while
              // the parent's looked deliberate.
              //
              // Why a negative marginLeft rather than raising the Stack's `spacing`:
              // MUI implements Stack spacing as a margin on the SECOND child, so
              // widening it would push the IMAGE right instead of pulling the ARROW
              // left — and the ask was to move the arrow. The -7/+7 pair moves the
              // glyph left by exactly the shortfall (16 - 9) while the negative
              // margin reclaims the same 7px from the flow, so the thumbnail stays
              // put at 191. Re-measure both edges if either value is touched; the
              // rendered gap is not the number you write (§40).
              <CornerDownRightIcon
                size={14}
                strokeWidth={1}
                color={theme.palette.text.primary}
                style={{ flexShrink: 0, marginLeft: -7, marginRight: 7 }}
              />
            )}
            {/* `showGroupBadge={!isChild}` suppresses CertificateCell's own
                child-role glyph (a plain diamond, Cells.tsx) so a child never
                carries TWO role icons — the down-right arrow above is this
                list's own, more specific "child" marker. A parent keeps
                `showGroupBadge` on unchanged (its role icon + member count).
                VRG-231 review round 2 item 1 — a child's text width was 200 while
                the column (434px) has room for far more once the tree column's own
                indent + expand-toggle reservation (~44px for a depth-1 row) and this
                row's own image/gap/icon budget are accounted for (~308px remains) —
                200 clipped both the title and the certificate ID needlessly. Matches
                the parent's own 260 now that there's room; measured against the
                column's actual budget, not eyeballed. */}
            <CertificateCell certificate={row} width={260} showGroupBadge={!isChild} />
          </Stack>
        )
      },
    }),
    [theme],
  )

  // VRG-231 fix E — see `removeFromGroupTarget`'s doc: does removing THIS
  // child leave its group with exactly one member (server-side auto-dissolve
  // territory)? `childrenByParentId` already holds the parent's FULL child
  // list (including the target itself), so exactly 2 there means exactly 1
  // remains after removal.
  const removeFromGroupSiblings = removeFromGroupTarget?.parent_certificate_id
    ? childrenByParentId.get(removeFromGroupTarget.parent_certificate_id) ?? []
    : []
  // VRG-231 review round 3 — a group of ONE is now a legitimate group, so removing a
  // child only dissolves the whole thing when it was the LAST child. Was `=== 2`
  // (i.e. "one would remain"), matching the server's old `<= 1` auto-dissolve rule.
  //
  // `removeFromGroupSiblings` is the parent's FULL child list INCLUDING the target,
  // so "the target is the only child" is length 1, not 0. Getting this backwards
  // either warns about a dissolve that will not happen or, worse, stays silent
  // through one that will — the warning and the server rule have to move together.
  const willDissolveGroupOnRemove = removeFromGroupSiblings.length === 1

  return (
    <>
      <Header
        title="Certificates"
        total={total ? total : undefined}
        rightComponents={
          <Stack direction="row" spacing={4} alignItems="center">
            { devToolsEnabled && (
              <Button
                variant="dev"
                onClick={handleCreateCertificateDev}
                startIcon={<BadgePlusIcon strokeWidth={1} />}
              >
                Create (No Scan Session)
              </Button>
            )}
            {/* data-tour: product-tour anchors (working-conventions §42).
                `check-tour-anchors.mjs` fails the build if these are renamed or
                removed while a tour still points at them. */}
            {/*
              Permission-gated like the other six Transfer affordances (§46: permission
              disables + names itself, record state hides). This was the ONE that gated
              nothing on the client — it opened the modal for anyone, let them pick a
              certificate and a recipient, and only then failed as a server 403 at
              submit. The server was never the problem; it always rejected. The cost was
              making the user do the whole task before being told they could not.

              Tooltip needs the <span> wrapper to have a hover target, because a disabled
              MUI Button fires no pointer events — same pattern as OwnerPage.
            */}
            <Tooltip title={!isCanTransfer ? TRANSFER_PERMISSION_TOOLTIP : ''}>
              <span>
                <Button
                  data-tour="certificates-transfer"
                  variant="secondary"
                  disabled={!isCanTransfer}
                  // Clears any prior bulk selection so the header button always opens the
                  // single-certificate picker, never a stale batch.
                  onClick={() => {
                    setBulkTransferRows([])
                    setOpenTransferModal(true)
                  }}
                  startIcon={<ArrowLeftRightIcon strokeWidth={1} />}
                >
                  Transfer
                </Button>
              </span>
            </Tooltip>
            <Button
              data-tour="certificates-create"
              onClick={handleCreateCertificates}
              startIcon={<BadgePlusIcon strokeWidth={1} />}
            >
              Create Certificates
            </Button>
          </Stack>
        }
      />
      {/* VRG-512: sample-data caveat — see ProductsPage's identical placement/comment.
          No `fillBottom`/`topOffset` on this page's `<Table>` either, so it's the same
          fixed-height case. */}
      <IntroDataBanner />
      <Table
        dataTour="certificates-table"
        columns={columns}
        // VRG-231 Tree Data — `treeRows` includes every fetched CHILD alongside
        // the top-level rows; `getTreeDataPath`/`groupingColDef` below tell
        // DataGridPro how to nest them. See both consts' own doc above.
        rows={treeRows}
        // ID and Product are redundant with the tree/Certificate cell (which
        // already shows the product name + ID) — hidden by default, still
        // reachable via the Columns menu for anyone who wants them. `certificate`
        // is ALSO hidden here (see that colDef's own note): the auto-inserted
        // tree column renders its content instead; it stays in `columns` only
        // for the Filters drawer's search target and CSV export.
        visibilityModel={{ id: false, product: false, certificate: false }}
        // Only show the loading/disabled state on the first load. On a
        // search/filter/page refetch we keep the existing rows + toolbar live
        // (the query hook has no keepPreviousData), so typing in search never
        // greys out the buttons or the field.
        loading={isLoading && certificates.length === 0}
        bulkActions={getBulkActions}
        alwaysShowRowSelection={false}
        // VRG-231 fix B — `rowSelectionModel`/`onRowSelectionModelChange` make
        // `gridSelectionModel` the grid's REAL, controlled selection state, so a
        // successful bulk action can actually clear the checkboxes + close the
        // bulk bar (see that state's own doc for why this is a SEPARATE state
        // from `selectedIds`, kept in sync via `onSelectionModelChange` exactly
        // as before).
        rowSelectionModel={gridSelectionModel as any}
        onRowSelectionModelChange={(model) => setGridSelectionModel(model as (string | number)[])}
        onSelectionModelChange={(ids) => setSelectedIds(ids as string[])}
        showSearch={true}
        search={searchValue}
        onSearchChange={handleSearchChange}
        // VERA-504, rebuilt by VRG-265 — the scan control beside the search box. It is one of the
        // target selectors, alongside the Certificate / Owner / Product / ID fields in the
        // Filters drawer: exactly one is lit, and the lit one is where the next tag lands (here,
        // as a tag-id search; there, as a checked option in that field's dropdown). The button
        // used to open a modal that held its own list of scanned items — two lists for one idea.
        showScan
        paginationMode="server"
        rowCount={total ?? -1}
        paginationModel={paginationModel}
        onPaginationModelChange={handlePaginationModelChange}
        fetchExportPage={fetchExportPage}
        onFiltersChange={handleFiltersChange}
        extraFilters={
          <Stack spacing={3}>
            <ToggleField
              label="Show archived"
              checked={showArchivedDraft}
              onChange={setShowArchivedDraft}
            />
          </Stack>
        }
        extraFiltersDirty={showArchivedDraft !== showArchived}
        onFiltersApply={() => {
          setShowArchived(showArchivedDraft)
          setPaginationModel(prev => ({ ...prev, page: 0 }))
        }}
        // VRG-231 — inline group expand via Tree Data (replacing the row Detail
        // Panel this used to be — see Table.tsx's `treeData` prop doc for why).
        // On the Community fallback (no hasMuiXLicense), Table.tsx trims `rows`
        // back down to top-level-only and never passes these three through to
        // DataGridCommunity at all — the group badge + member count on the
        // certificate cell are the fallback affordance in that case.
        treeData
        getTreeDataPath={getTreeDataPath}
        groupingColDef={groupingColDef}
        // Clear owns the extras too — reset the draft AND the applied value,
        // or the next commit bounces the toggle back on.
        onClearExtraFilters={() => {
          setShowArchivedDraft(false)
          setShowArchived(false)
          setPaginationModel(prev => ({ ...prev, page: 0 }))
        }}
        emptyState={
          hasActiveFilters ? (
            <EmptyState
              title="No certificates found"
              text="No certificates match the current filters"
              icon={<BadgeXIcon strokeWidth={1} size={32} />}
              buttons={<AskAboutButton question="How many certificates do I have in total?" />}
            />
          ) : (
            <EmptyState
              title="No certificates created"
              text="Create certificates individually or in bulk using the actions above"
              icon={<BadgeXIcon strokeWidth={1} size={32} />}
              />
          )
        }
        viewKey="certificates"
      />

      <CreateCertificatesModal
        product={selectedCertificate?.product || null}
        open={openCreateCertificatesModal}
        refetch={handleCertificatesCreated}
        onClose={handleCloseCreateCertificatesModal}
      />
      <CreateCertificateDevModal
        open={openCreateCertificateDevModal}
        refetch={handleCertificatesCreated}
        onClose={handleCloseCreateCertificateDevModal}
      />
      {/*
        VRG-512 — offered AFTER the create has already succeeded, never in front of it.
        The hook decides whether it fires at all (first real certificate, samples still
        present, user holds `sample_data_manage`); this just has to be mounted.
      */}
      <SampleSectionClearPrompt {...samplePrompt.promptProps} />
      {/*
        VRG-836 — "Convert to real". The gate that names the COST: converting a demo
        certificate records the activation, so it lands on the next invoice, and
        `sample_data_manage`'s own description ("samples are never billed") does not say
        so. See `ConvertDemoRowModal` for why the confirm carries that rather than a
        second permission.
      */}
      <ConvertDemoRowModal
        kind="certificate"
        open={Boolean(convertDemoCertificate)}
        onClose={() => setConvertDemoCertificate(null)}
        onConfirm={handleConfirmConvertDemoCertificate}
        loading={isConvertingDemoCertificate}
        name={convertDemoCertificate?.product?.title}
        error={convertDemoCertificateError}
      />
      <DeleteCertificateModal
        certificate={selectedCertificate}
        open={openDeleteModal}
        onClose={handleCloseDeleteModal}
        refetch={refetch}
      />
      {/*
        VRG-512 (AC4) — "Remove sample" confirm. `ConfirmSettingModal`, not
        `DeleteCertificateModal`: this hits `DELETE /intro-data/certificates/:id`, a
        different use-case from the real certificate delete (no owner/status
        precondition — a sample cannot have a real owner, and the endpoint's own WHERE
        clause is the actual safeguard, not this modal).
      */}
      <ConfirmSettingModal
        open={Boolean(removeSampleCertificate)}
        onClose={() => setRemoveSampleCertificate(null)}
        onConfirm={handleConfirmRemoveSampleCertificate}
        loading={isDeletingIntroCertificate}
        destructive
        title="Remove this sample certificate?"
        consequence={
          'This sample certificate and its scan history will be deleted. It does not ' +
          'affect any of your real certificates or products.'
        }
        confirmLabel="Remove sample"
      >
        {deleteIntroCertificateError && (
          <Typography variant="body3" color="error.main">
            {getErrorMessage(deleteIntroCertificateError)}
          </Typography>
        )}
      </ConfirmSettingModal>
      <BulkDeleteCertificatesModal
        certificates={certificates.filter(c => selectedIds.includes(c.id))}
        totalCount={selectedIds.length}
        open={openBulkDeleteModal}
        onClose={handleCloseBulkDeleteModal}
        onConfirm={() => {
          const ids = selectedIds
          bulkRemove({ ids })
        }}
        isLoading={isBulkDeleting}
      />
      {/*
        VRG-528 AC-12 — the number shown MUST be the number acted on.
        `selectedIds.length` is the whole selection, which can include
        certificates that are already live: those are not activated and are not
        charged, so counting them made the modal overstate both the action and
        the spend. "Activate 5 certificates?" over a selection where 2 are
        already active is worse than no gate, because it launders volume through
        a control that only looks like it asked.

        The same filtered set drives the confirm, so the ids sent match the
        number shown rather than relying on the server to quietly drop the rest.
      */}
      {/* VERA-931 — one instance for the whole list; the row kebab sets which
          certificate it is acting on. */}
      <SendToLocationModal
        open={Boolean(custodyRow)}
        onClose={() => setCustodyRow(null)}
        certificateId={custodyRow?.id ?? ''}
        resolvedTrigger={
          custodyRow?.activation_trigger ??
          custodyRow?.product?.activation_trigger ??
          null
        }
        loading={custody.isDispatching}
        onSend={addressId =>
          custodyRow &&
          custody.sendToStore({certificateId: custodyRow.id, addressId})
        }
      />
      <ConfirmSettingModal
        open={Boolean(confirmArrivalRow)}
        onClose={() => setConfirmArrivalRow(null)}
        onConfirm={() => {
          const hop = confirmArrivalRow && openHopFor(confirmArrivalRow.id)
          if (!hop || !confirmArrivalRow) return
          custody.confirmReceipt({
            custodyEventId: hop.id,
            certificateId: confirmArrivalRow.id,
            method: 'transferred',
          })
        }}
        title="Confirm this arrived?"
        consequence="Confirming arrival adds the location to this certificate's chain of custody, permanently. If this certificate activates on arrival, confirming also activates it, and activation is what makes it billable."
        confirmLabel="Confirm arrival"
        loading={custody.isReceiving}
      />

      <BulkActivationModal
        certificates={bulkActivatableCertificates}
        totalCount={bulkActivatableCertificates.length}
        open={openBulkActivateModal}
        onClose={handleCloseBulkActivateModal}
        onConfirm={() => {
          bulkActivate({ ids: bulkActivatableCertificates.map(c => c.id) })
        }}
        isLoading={isBulkActivating}
      />
      {/*
        One modal serves both the single and the bulk path. `bulkTransferRows` is
        non-empty only for the bulk action, and the modal switches on that — so the
        header button and the row kebab keep passing `certificate` and behave exactly
        as before.
      */}
      <TransferCertificateModal
        open={openTransferModal}
        certificate={selectedCertificate}
        certificates={bulkTransferRows}
        onClose={handleCloseTransferModal}
        refetch={refetch}
      />
      <CancelTransferModal
        open={openCancelTransferModal}
        certificate={selectedCertificate}
        onClose={handleCloseCancelTransferModal}
        refetch={refetch}
      />
      <ReplaceTagModal
        certificate={selectedCertificate}
        open={openReplaceTagModal}
        onClose={handleCloseReplaceTagModal}
        refetch={refetch}
      />
      {/* VRG-231 — was a hand-rolled Modal here (and a near-identical second
          copy on CertificatePage) whose whole group treatment was one
          number-less sentence shown only on a group PARENT. Both facts changed
          when the activation cascade became symmetric: a MEMBER cascades too,
          and the user is owed the count before the charge, not after. One
          shared modal now, per §36. */}
      <ActivateCertificateModal
        certificate={selectedCertificate}
        open={openActivateModal}
        onClose={handleCloseActivateModal}
        onConfirm={handleConfirmActivate}
        isLoading={mutationState.isActivating}
      />

      <Modal
        title="Archive certificate"
        open={openArchiveModal}
        onClose={handleCloseArchiveModal}
        actions={
          <>
            <Button
              disableElevation
              variant="secondary"
              onClick={handleCloseArchiveModal}
              disabled={isArchiving}
            >
              Cancel
            </Button>
            <Button
              disableElevation
              onClick={handleConfirmArchive}
              loading={isArchiving}
              disabled={isArchiving}
            >
              Archive
            </Button>
          </>
        }
      >
        <Stack spacing={4}>
          <Stack direction="row" spacing={2.5} alignItems="flex-start">
            <ArchiveIcon size={32} color={theme.palette.warning.main} strokeWidth={1} />
            <Stack spacing={1}>
              <Typography variant="body1" sx={{ fontSize: 16, fontWeight: 400 }}>
                Archive this certificate? It will be hidden from the default view but can be restored.
              </Typography>
            </Stack>
          </Stack>
        </Stack>
      </Modal>

      <CreateCertificateGroupModal
        open={openCreateGroupModal}
        onClose={handleCloseCreateGroupModal}
        initialCertificate={selectedCertificate}
        initialCertificates={groupModalInitialCertificates ?? undefined}
        existingParent={groupModalExistingParent}
        onCreated={handleGroupCreated}
      />

      <SplitGroupModal
        open={openSplitGroupModal}
        onClose={handleCloseSplitGroupModal}
        certificate={selectedCertificate}
        children={splitGroupData?.children ?? []}
        onSplit={handleSplitGroupSuccess}
      />

      {/* VRG-231 fix E — a CHILD's own "Remove from group" (fix D) used to fire
          the unlink immediately on click with no confirmation. This discloses
          the effect BEFORE it commits — including the full-group dissolve the
          server applies when removal would leave exactly one certificate
          behind (unlink-certificates-from-group.use-case.ts). */}
      <Modal
        title="Remove from group"
        open={openRemoveFromGroupModal}
        onClose={handleCloseRemoveFromGroupModal}
        actions={
          <>
            <Button
              disableElevation
              variant="secondary"
              onClick={handleCloseRemoveFromGroupModal}
              disabled={isUnlinking}
            >
              Cancel
            </Button>
            <Button
              disableElevation
              onClick={handleConfirmRemoveFromGroup}
              loading={isUnlinking}
              disabled={isUnlinking}
            >
              {willDissolveGroupOnRemove ? 'Remove and dissolve group' : 'Remove'}
            </Button>
          </>
        }
      >
        <Stack spacing={4}>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
            Remove "{removeFromGroupTarget?.product?.title ?? 'this certificate'}" from its
            certificate group? It will become a standalone certificate again.
          </Typography>
          {willDissolveGroupOnRemove && (
            // VRG-231 review round 3 — rewritten for the new rule. This used to fire
            // when ONE member would remain and name that survivor. It now fires only
            // when NONE would, so there is no survivor to name and the consequence is
            // about the parent, which is the thing that actually disappears.
            <Alert
              severity="warning"
              description="This is the group's last member. Removing it dissolves the group, and the parent certificate is archived."
            />
          )}
        </Stack>
      </Modal>
    </>
  )
}
